import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  SummaryListComponent,
  SummaryListField,
  SummaryListActionConfig,
  ComponentType,
} from "../../services/types/ComponentTypes";
import { apiService, LookupOption } from "../../services/api";
import { useAppSelector } from "../../state/hooks";
import { ReportRawDataLoader } from "./ReportRawDataLoader";
import { TimelineModal, WorkflowStep } from "./TimelineModal";
import { CardView } from "./CardView";
import { SearchableDropdown } from "./SearchableDropdown";
import { getTablePreferences, saveTablePreferences, clearTablePreferences } from "../../services/userPreferences";
import { fileService, isFileKey, isImageId, openImageById } from "../../services/fileService";
import { isComponentVisible } from "../../utils/visibilityUtils";
// Removed openPDFViewer import - PDFs now open directly in browser

// Report filter types
export interface ReportFilter {
  Label: string;
  ControlType: "Date" | "Select" | "Search_Select";
  IsMandatory: boolean;
  BindValue: string;
  showInSummary?: boolean;
  IsClear?: boolean;
  IsVerticalView?: boolean;
  IncludeLabel?: boolean;
  PlaceHolder?: string;
  Default_Value?: string;
  LookupName?: string;
}

export interface ReportData {
  id: number;
  ReportName: string;
  ReportSettings: string;
  Filters: ReportFilter[];
}

const getSummaryActionIcon = (
  actionType: string,
  size: number
): React.ReactNode => {
  switch (actionType) {
    case "edit":
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
        </svg>
      );
    case "delete":
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
        </svg>
      );
    case "pdf_download":
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
        </svg>
      );
    case "view":
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
        </svg>
      );
    case "view_report":
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
        </svg>
      );
    case "lock":
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      );
    case "print":
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
        </svg>
      );
    case "approve":
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M20 6L9 17l-5-5" />
        </svg>
      );
    default:
      return null;
  }
};

const getWorkflowStatusIcon = (
  status: "completed" | "pending" | "upcoming" | "default",
  size: number
): React.ReactNode => {
  switch (status) {
    case "completed":
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M20 6L9 17l-5-5" />
        </svg>
      );
    case "pending":
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M12 6v6l4 2" />
        </svg>
      );
    case "upcoming":
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="10" />
        </svg>
      );
    case "default":
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="3" />
        </svg>
      );
    default:
      return null;
  }
};

export interface DataTableFetchParams {
  skip: number;
  limit: number;
  search: Array<{ value: string; name: string; controltype: string }>;
  sort: Array<{ field: string; value: "asc" | "desc" }>;
}

export interface DataTableFetchResponse {
  success: boolean;
  data: Record<string, any>[];
  totalcount?: number;
}

// Report Filter Dialog Component
interface ReportFilterDialogProps {
  report: ReportData;
  isOpen: boolean;
  onClose: () => void;
  onGenerate: (reportSettings: string, filterValues: Record<string, any>, outputType: "Excel" | "PDF" | "RawData") => void;
  isGenerating: boolean;
}

const ReportFilterDialog: React.FC<ReportFilterDialogProps> = ({
  report,
  isOpen,
  onClose,
  onGenerate,
  isGenerating,
}) => {
  const [filterValues, setFilterValues] = useState<Record<string, any>>({});
  // Previous code: Dropdown for output type selection
  // const [outputType, setOutputType] = useState<"Excel" | "PDF">("Excel");
  const [lookupOptions, setLookupOptions] = useState<Record<string, LookupOption[]>>({});
  const [loadingLookups, setLoadingLookups] = useState<Set<string>>(new Set());

  // Initialize filter values with defaults
  useEffect(() => {
    if (isOpen && report) {
      const initialValues: Record<string, any> = {};
      report.Filters?.forEach((filter) => {
        if (filter.Default_Value) {
          initialValues[filter.BindValue] = filter.Default_Value;
        } else if (filter.ControlType === "Date") {
          initialValues[filter.BindValue] = "";
        }
      });
      setFilterValues(initialValues);
      // Previous code: Reset output type to Excel when dialog opens
      // setOutputType("Excel");
    }
  }, [isOpen, report]);

  // Fetch lookup options for Select and Search_Select filters
  useEffect(() => {
    if (!isOpen || !report || !report.Filters) return;

    const lookupFilters = report.Filters.filter(
      (f) => (f.ControlType === "Select" || f.ControlType === "Search_Select") && f.LookupName
    );

    lookupFilters.forEach(async (filter) => {
      if (!filter.LookupName || lookupOptions[filter.LookupName]) return;

      setLoadingLookups((prev) => new Set(prev).add(filter.LookupName!));

      try {
        const options = await apiService.getLookupData(filter.LookupName, {
          parentid: "",
          lookuptype: filter.ControlType,
        });
        setLookupOptions((prev) => ({
          ...prev,
          [filter.LookupName!]: options,
        }));
      } catch (error) {
        console.error(`Failed to fetch lookup ${filter.LookupName}:`, error);
      } finally {
        setLoadingLookups((prev) => {
          const next = new Set(prev);
          next.delete(filter.LookupName!);
          return next;
        });
      }
    });
  }, [isOpen, report, lookupOptions]);

  const handleFilterChange = (bindValue: string, value: any) => {
    setFilterValues((prev) => ({
      ...prev,
      [bindValue]: value,
    }));
  };

  // Previous code: Single download button with dropdown selection
  // const handleGenerate = () => {
  //   onGenerate(report.ReportSettings, filterValues, outputType);
  // };

  // New code: Separate buttons for each output type
  const handleGenerate = (outputType: "Excel" | "PDF" | "RawData") => {
    onGenerate(report.ReportSettings, filterValues, outputType);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Dialog */}
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {report.ReportName}
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-4 max-h-[60vh] overflow-y-auto">
          {report.Filters?.map((filter) => (
            <div key={filter.BindValue} className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {filter.Label}
                {filter.IsMandatory && <span className="text-danger-500 ml-1">*</span>}
              </label>

              {filter.ControlType === "Date" && (
                <input
                  type="date"
                  value={filterValues[filter.BindValue] || ""}
                  onChange={(e) => handleFilterChange(filter.BindValue, e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors"
                />
              )}

              {(filter.ControlType === "Select" || filter.ControlType === "Search_Select") && (
                <div className="-mx-2">
                  <SearchableDropdown
                    config={{
                      name: filter.BindValue,
                      label: "",
                      required: false,
                      placeholder: loadingLookups.has(filter.LookupName || "") ? "Loading..." : (filter.PlaceHolder || "Select..."),
                      options: filter.LookupName ? (lookupOptions[filter.LookupName] || []) : [],
                      disabled: loadingLookups.has(filter.LookupName || ""),
                      multiple: false,
                      clearable: filter.IsClear !== false,
                    } as any}
                    value={filterValues[filter.BindValue] || ""}
                    onChange={(name, value) => handleFilterChange(name, value as string)}
                  />
                </div>
              )}
            </div>
          ))}

          {/* Previous code: Output Type Dropdown Selection */}
          {/* <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Output Type
            </label>
            <select
              value={outputType}
              onChange={(e) => setOutputType(e.target.value as "Excel" | "PDF")}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors"
            >
              <option value="Excel">Excel</option>
              <option value="PDF">PDF</option>
            </select>
          </div> */}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          {/* <button
            onClick={onClose}
            disabled={isGenerating}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors disabled:opacity-70"
          >
            Close
          </button> */}

          {/* Previous code: Single Download Button with Dropdown Selection */}
          {/* <button
            onClick={handleGenerate}
            disabled={isGenerating}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 rounded-lg transition-colors disabled:opacity-70 flex items-center gap-2"
          >
            {isGenerating ? (
              <>
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Generating...
              </>
            ) : (
              <>Download</>
            )}
          </button> */}
          {/* New code: Separate Download Buttons for PDF, Excel, and Raw Data */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleGenerate("PDF")}
              disabled={isGenerating}
              className="px-4 py-2 whitespace-nowrap text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-70 flex items-center gap-2"
            >
              {isGenerating ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Generating...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  Download PDF
                </>
              )}
            </button>
            {/* <button
              onClick={() => handleGenerate("Excel")}
              disabled={isGenerating}
              className="px-4 py-2 whitespace-nowrap text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-70 flex items-center gap-2"
            >
              {isGenerating ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Generating...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download Excel
                </>
              )}
            </button> */}
            <button
              onClick={() => handleGenerate("RawData")}
              disabled={isGenerating}
              className="px-4 py-2 whitespace-nowrap text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-70 flex items-center gap-2"
            >
              {isGenerating ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Loading...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
                  </svg>
                  View Data
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface DataTableProps {
  config: SummaryListComponent;
  onActionTrigger?: (actionId: string, data?: any) => void;
  dataFetcher?: (
    params: DataTableFetchParams
  ) => Promise<DataTableFetchResponse>;
  data?: Record<string, any>[];
}

// File View Button Component (for non-image files)
const FileViewButton: React.FC<{
  attachmentUrl: string;
  fieldLabel?: string;
  isFile: boolean;
}> = ({ attachmentUrl, fieldLabel, isFile }) => {
  const [isLoading, setIsLoading] = React.useState(false);

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!attachmentUrl) {
      return;
    }

    setIsLoading(true);

    try {
      if (isImageId(attachmentUrl)) {
        // Image ID from DB: fetch via utility/image/{id}/download (streaming)
        await openImageById(attachmentUrl);
      } else if (isFile) {
        // Use presigned URL service for private files (files/... or uploads/...)
        await fileService.openFile(attachmentUrl);
      } else {
        // Absolute URL, open directly
        window.open(attachmentUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (error: any) {
      console.error('[DataTable] Error opening file:', error);
      // Show error notification if available
      alert(error.message || 'Failed to open file. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isLoading}
      className="inline-flex items-center justify-center p-1.5 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded transition-colors focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:outline-none disabled:opacity-70 disabled:cursor-not-allowed"
      title={`View ${fieldLabel || 'attachment'}`}
      aria-label={`View ${fieldLabel || 'attachment'}`}
    >
      {isLoading ? (
        <svg
          className="w-5 h-5 animate-spin"
          fill="none"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      ) : (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
          />
        </svg>
      )}
    </button>
  );
};

export const DataTable: React.FC<DataTableProps> = ({
  config,
  onActionTrigger,
  dataFetcher,
  data: externalData,
}) => {
  // Removed dispatch - PDFs now open directly in browser
  // Infinite scroll state
  const [loadedData, setLoadedData] = useState<Record<string, any>[]>([]);
  const [skip, setSkip] = useState(0);
  const [pageSize] = useState(20); // Batch size for infinite scroll
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const [filters, setFilters] = useState<Record<string, string>>({});
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc";
  } | null>(null);
  // State for card view search dropdown
  const [selectedSearchField, setSelectedSearchField] = useState<string>("");
  const selectedSearchFieldRef = React.useRef<string>("");
  const userSelectedFieldRef = React.useRef<boolean>(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [data, setData] = useState<Record<string, any>[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
  const [columnPinning, setColumnPinning] = useState<Record<string, boolean>>({});
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
  const tableRef = React.useRef<HTMLTableElement>(null);
  const tableBodyRef = React.useRef<HTMLDivElement>(null);
  const settingsPopupRef = React.useRef<HTMLDivElement>(null);
  const [settingsPopupPosition, setSettingsPopupPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Lookup options for summary-list dropdown fields (e.g., Employees -> Group)
  const [fieldLookupOptions, setFieldLookupOptions] = useState<Record<string, LookupOption[]>>({});
  const [fieldLoadingLookups, setFieldLoadingLookups] = useState<Set<string>>(new Set());

  // Report dialog state (for EnableReport grids)
  const [selectedReport, setSelectedReport] = useState<ReportData | null>(null);
  const [isReportDialogOpen, setIsReportDialogOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const authUserId = useAppSelector((state) => state.auth?.user?.id);

  // Raw data loader: when set, ReportRawDataLoader is open and runs the load query
  const [rawDataLoaderParams, setRawDataLoaderParams] = useState<{
    reportName: string;
    reportSettings: string;
    filterValues: Record<string, any>;
  } | null>(null);

  // Timeline modal state (for inspection screen)
  const [selectedTimelineRow, setSelectedTimelineRow] = useState<Record<string, any> | null>(null);
  const [isTimelineModalOpen, setIsTimelineModalOpen] = useState(false);
  const [timelineAnchorElement, setTimelineAnchorElement] = useState<HTMLElement | null>(null);
  const progressBarRefs = React.useRef<Map<number, HTMLDivElement>>(new Map());

  // Check if this is a report-enabled grid
  const isReportGrid = !!(config as any).EnableReport;

  // Check if this is the inspection screen (for backward compatibility)
  const isInspectionScreen = (config.Form?.Name || "").toLowerCase() === "vehiclein_summary";

  // Generate unique table ID for preferences
  const tableId = useMemo(() => {
    const displayName = config.DisplayName || config.Form?.Name || "DataTable";
    const fields = config.fields ?? config.Form?.Fields ?? [];
    const fieldKeys = fields.map((f: SummaryListField) => f.Json_Attribute || f.BindValue || "").join(",");
    return `${displayName}-${fieldKeys}`.replace(/[^a-zA-Z0-9-]/g, "-");
  }, [config.DisplayName, config.Form?.Name, config.fields, config.Form?.Fields]);

  // View mode state (table or card) - available for all datatables
  const [viewMode, setViewMode] = useState<"table" | "card">("table");

  // Table view mode state (progress or standard) - available when workflowActions exist
  const [tableViewMode, setTableViewMode] = useState<"progress" | "standard">("progress");

  // Load view mode preferences on mount
  useEffect(() => {
    try {
      const tablePrefs = getTablePreferences(tableId);
      if (tablePrefs) {
        if (tablePrefs.viewMode === "table" || tablePrefs.viewMode === "card") {
          setViewMode(tablePrefs.viewMode);
        }
        if (tablePrefs.tableViewMode === "progress" || tablePrefs.tableViewMode === "standard") {
          setTableViewMode(tablePrefs.tableViewMode);
        }
      }
    } catch (error) {
      console.warn("[DataTable] Failed to load view mode preferences:", error);
    }
  }, [tableId]);

  const useServerSidePagination = !!dataFetcher;

  const workflowActions = config.workflow ?? [];

  // Check if workflow actions exist (for progress view)
  const hasWorkflowActions = workflowActions.length > 0;
  const summaryActions = config.SummeryActions ?? [];
  const hasActionColumn =
    workflowActions.length > 0 || summaryActions.length > 0;

  const normalizedWorkflowStates = useMemo(
    () =>
      workflowActions.map((action) =>
        (action.States ?? []).map((state) => String(state).trim().toLowerCase())
      ),
    [workflowActions]
  );

  const workflowColorPalette = useMemo(
    () => ({
      completed: "var(--color-primary-500, #35ba5b)",
      pending: "#F59E0B",
      upcoming: "#E5E7EB",
      default: "#3B82F6",
      rejected: "#DC2626", // Red for rejected status
      textLight: "#FFFFFF",
      textDark: "#1F2937",
    }),
    []
  );

  useEffect(() => {
    // Use a flag to track if component is still mounted
    let isMounted = true;

    const fetchInitialData = async () => {
      if (dataFetcher) {
        setLoading(true);
        setError(null);
        setSkip(0);
        setLoadedData([]);
        setHasMore(true);
        try {
          const searchArray: Array<{
            value: string;
            name: string;
            controltype: string;
          }> = [];
          Object.entries(filters).forEach(([key, value]) => {
            if (value) {
              const field = visibleFields.find(
                (f: SummaryListField) => f.Json_Attribute === key
              );
              let finalValue = value;
              let finalControlType = field?.ControlType || "TextView";

              if (field?.componentType === ComponentType.DATE_FIELD) {
                if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
                  const parts = value.split("T")[0].split("-");
                  if (parts.length === 3) {
                    finalValue = `${parts[2]}-${parts[1]}-${parts[0]}`;
                  }
                }
                finalControlType = "DateTime";
              }

              searchArray.push({
                value: finalValue,
                name: key,
                controltype: finalControlType,
              });
            }
          });

          const sortArray: Array<{ field: string; value: "asc" | "desc" }> =
            [];
          if (sortConfig) {
            sortArray.push({
              field: sortConfig.key,
              value: sortConfig.direction,
            });
          }

          const fetchParams: DataTableFetchParams = {
            skip: 0,
            limit: pageSize,
            search: searchArray,
            sort: sortArray,
          };

          const response = await dataFetcher(fetchParams);
          if (isMounted) {
            if (response.success) {
              const responseData = response.data || [];
              setLoadedData(responseData);
              setData(responseData);
              setTotalCount(response.totalcount || responseData.length || 0);
              setHasMore(responseData.length === pageSize && (response.totalcount || 0) > responseData.length);
            } else {
              setError("Failed to load data");
              setData([]);
              setLoadedData([]);
              setTotalCount(0);
              setHasMore(false);
            }
          }
        } catch (err) {
          console.error("Error fetching data:", err);
          if (isMounted) {
            setError(
              err instanceof Error ? err.message : "Failed to load data"
            );
            setData([]);
            setLoadedData([]);
            setTotalCount(0);
            setHasMore(false);
          }
        } finally {
          if (isMounted) {
            setLoading(false);
          }
        }
      } else if (externalData !== undefined) {
        if (isMounted) {
          setData(externalData);
          setLoadedData(externalData);
          setTotalCount(externalData.length);
          setHasMore(false);
        }
      } else {
        if (isMounted) {
          setData([]);
          setLoadedData([]);
          setTotalCount(0);
          setHasMore(false);
        }
      }
    };

    fetchInitialData();

    return () => {
      isMounted = false;
    };
  }, [
    dataFetcher,
    externalData,
    filters,
    sortConfig,
    pageSize,
  ]);

  // Save column visibility, pinning, and view mode preferences to unified preferences service
  const saveTablePrefs = useCallback((
    visibility?: Record<string, boolean>,
    pinning?: Record<string, boolean>,
    viewModePref?: "table" | "card",
    tableViewModePref?: "progress" | "standard"
  ) => {
    try {
      saveTablePreferences(tableId, {
        columnVisibility: visibility !== undefined ? visibility : columnVisibility,
        columnPinning: pinning !== undefined ? pinning : columnPinning,
        viewMode: viewModePref !== undefined ? viewModePref : viewMode,
        tableViewMode: tableViewModePref !== undefined ? tableViewModePref : tableViewMode,
      });
    } catch (error) {
      console.warn("[DataTable] Failed to save table preferences:", error);
    }
  }, [tableId, columnVisibility, columnPinning, viewMode, tableViewMode]);

  // Load column visibility and pinning preferences from unified preferences service
  useEffect(() => {
    try {
      const tablePrefs = getTablePreferences(tableId);
      if (tablePrefs) {
        if (tablePrefs.columnVisibility && Object.keys(tablePrefs.columnVisibility).length > 0) {
          setColumnVisibility(tablePrefs.columnVisibility);
        }
        if (tablePrefs.columnPinning && Object.keys(tablePrefs.columnPinning).length > 0) {
          setColumnPinning(tablePrefs.columnPinning);
        }
      }
    } catch (error) {
      console.warn("[DataTable] Failed to load table preferences:", error);
    }
  }, [tableId]);

  const allFields = useMemo(() => {
    const fields = config.fields ?? config.Form?.Fields ?? [];
    return fields.filter(
      (field: SummaryListField) => field.showInSummary
    );
  }, [config.fields, config.Form?.Fields]);

  const visibleFields = useMemo(() => {
    // If no visibility preferences are set, show all fields
    if (Object.keys(columnVisibility).length === 0) {
      return allFields;
    }

    // Filter fields based on visibility preferences
    return allFields.filter((field: SummaryListField) => {
      const fieldKey = field.Json_Attribute || field.BindValue || "";
      // Default to visible (true) if not set in preferences
      return columnVisibility[fieldKey] !== false;
    });
  }, [allFields, columnVisibility]);

  // Fetch lookup options for any visible field that has LookupName and is a dropdown.
  useEffect(() => {
    const dropdownFields = visibleFields.filter(
      (f) =>
        !!f.LookupName &&
        (f.componentType === ComponentType.SELECT_DROPDOWN ||
          f.componentType === ComponentType.SEARCHABLE_DROPDOWN ||
          f.ControlType === "Search_Select")
    );

    const uniqueLookupNames = Array.from(
      new Set(dropdownFields.map((f) => f.LookupName!).filter(Boolean))
    );

    uniqueLookupNames.forEach(async (lookupName) => {
      if (fieldLookupOptions[lookupName] || fieldLoadingLookups.has(lookupName)) return;

      setFieldLoadingLookups((prev) => new Set(prev).add(lookupName));
      try {
        const options = await apiService.getLookupData(lookupName, {
          parentid: "",
          lookuptype: "Select",
        });
        setFieldLookupOptions((prev) => ({ ...prev, [lookupName]: options || [] }));
      } catch (error) {
        console.error(`[DataTable] Failed to fetch lookup ${lookupName}:`, error);
      } finally {
        setFieldLoadingLookups((prev) => {
          const next = new Set(prev);
          next.delete(lookupName);
          return next;
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleFields]);

  const getLookupLabel = useCallback(
    (field: SummaryListField, rawValue: any): string | null => {
      const lookupName = field.LookupName;
      if (!lookupName) return null;
      const options = fieldLookupOptions[lookupName];
      if (!options || options.length === 0) return null;
      const match = options.find((o) => String(o.value) === String(rawValue));
      return match ? String(match.label) : null;
    },
    [fieldLookupOptions]
  );

  // Initialize selectedSearchField when visibleFields change (only if user hasn't manually selected)
  const initializedRef = React.useRef(false);
  useEffect(() => {
    if (visibleFields.length > 0 && !initializedRef.current && !userSelectedFieldRef.current) {
      const firstField = visibleFields[0];
      const firstFieldKey = firstField?.Json_Attribute || firstField?.BindValue || "";
      if (firstFieldKey) {
        setSelectedSearchField(firstFieldKey);
        selectedSearchFieldRef.current = firstFieldKey;
        initializedRef.current = true;
      }
    } else if (visibleFields.length > 0 && !userSelectedFieldRef.current && selectedSearchField) {
      // If selectedSearchField is no longer in visibleFields, reset to first field
      const fieldExists = visibleFields.find(f => (f.Json_Attribute || f.BindValue) === selectedSearchField);
      if (!fieldExists) {
        const firstField = visibleFields[0];
        const firstFieldKey = firstField?.Json_Attribute || firstField?.BindValue || "";
        if (firstFieldKey) {
          setSelectedSearchField(firstFieldKey);
          selectedSearchFieldRef.current = firstFieldKey;
        }
      }
    }
  }, [visibleFields.length, selectedSearchField]); // Include selectedSearchField to check if it's still valid

  // Calculate sticky column positions (includes pinned columns)
  const stickyColumnInfo = useMemo(() => {
    const info: Map<number, { position: "left" | "right"; left: number; width: string }> = new Map();
    let leftOffset = 0;

    // Process left sticky columns (both from config and pinned)
    visibleFields.forEach((field: SummaryListField, index: number) => {
      const stickyValue = field.StickyColumn;
      const isPinned = columnPinning[field.Json_Attribute] === true;

      if (stickyValue === "left" || stickyValue === true || isPinned) {
        const width = field.StickyWidth || "200px";
        info.set(index, {
          position: "left",
          left: leftOffset,
          width: width,
        });
        // Calculate next offset (parse width and add)
        const widthNum = parseInt(width.replace("px", "")) || 200;
        leftOffset += widthNum;
      }
    });

    return info;
  }, [visibleFields, columnPinning]);

  // Check if a column is sticky
  const isStickyColumn = (fieldIndex: number, position?: "left" | "right"): boolean => {
    const info = stickyColumnInfo.get(fieldIndex);
    if (!info) return false;
    if (position) return info.position === position;
    return true;
  };

  // Get sticky column info
  const getStickyInfo = (fieldIndex: number) => {
    return stickyColumnInfo.get(fieldIndex);
  };

  const handleToggleColumnVisibility = (fieldKey: string) => {
    const newVisibility = {
      ...columnVisibility,
      [fieldKey]: !(columnVisibility[fieldKey] !== false), // Toggle: default true becomes false, false becomes true
    };
    setColumnVisibility(newVisibility);
    saveTablePrefs(newVisibility, undefined);
  };

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // Close column settings popup on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && showColumnSettings) {
        setShowColumnSettings(false);
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [showColumnSettings]);

  const filteredData = useMemo(() => {
    if (useServerSidePagination) {
      return data;
    }

    let filtered = [...data];

    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        // Handle workflow status filter
        if (key === "__workflow_status") {
          filtered = filtered.filter((item) => {
            const lastAction = item?.last_action ?? item?.lastAction ?? "";
            return String(lastAction).trim().toLowerCase() === String(value).trim().toLowerCase();
          });
        } else {
          // Regular field filter
          filtered = filtered.filter((item) =>
            String(item[key]).toLowerCase().includes(value.toLowerCase())
          );
        }
      }
    });

    if (sortConfig) {
      filtered.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];
        if (aValue < bValue) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  }, [data, filters, sortConfig, useServerSidePagination]);

  // Use loadedData directly for infinite scroll (no pagination)
  const displayData = useMemo(() => {
    if (useServerSidePagination) {
      return loadedData;
    }
    return filteredData;
  }, [loadedData, filteredData, useServerSidePagination]);

  // Load more data function for infinite scroll
  const loadMoreData = React.useCallback(async () => {
    if (!dataFetcher || isLoadingMore || !hasMore || loading) return;

    setIsLoadingMore(true);
    try {
      const searchArray: Array<{
        value: string;
        name: string;
        controltype: string;
      }> = [];
      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          const field = visibleFields.find(
            (f: SummaryListField) => f.Json_Attribute === key
          );
              let finalValue = value;
              let finalControlType = field?.ControlType || "TextView";

              if (field?.componentType === ComponentType.DATE_FIELD) {
                if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
                  const parts = value.split("T")[0].split("-");
                  if (parts.length === 3) {
                    finalValue = `${parts[2]}-${parts[1]}-${parts[0]}`;
                  }
                }
                finalControlType = "DateTime";
              }

              searchArray.push({
                value: finalValue,
                name: key,
                controltype: finalControlType,
              });
            }
      });

      const sortArray: Array<{ field: string; value: "asc" | "desc" }> = [];
      if (sortConfig) {
        sortArray.push({
          field: sortConfig.key,
          value: sortConfig.direction,
        });
      }

      const nextSkip = loadedData.length;
      const fetchParams: DataTableFetchParams = {
        skip: nextSkip,
        limit: pageSize,
        search: searchArray,
        sort: sortArray,
      };

      const response = await dataFetcher(fetchParams);
      if (response.success) {
        const responseData = response.data || [];
        if (responseData.length > 0) {
          setLoadedData((prev) => [...prev, ...responseData]);
          setData((prev) => [...prev, ...responseData]);
          setHasMore(responseData.length === pageSize && (response.totalcount || 0) > loadedData.length + responseData.length);
        } else {
          setHasMore(false);
        }
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error("Error loading more data:", err);
      setHasMore(false);
    } finally {
      setIsLoadingMore(false);
    }
  }, [dataFetcher, isLoadingMore, hasMore, loading, filters, sortConfig, loadedData.length, pageSize, visibleFields]);

  // Infinite scroll handler
  useEffect(() => {
    const handleScroll = () => {
      if (!tableBodyRef.current || !hasMore || isLoadingMore || loading) return;

      const { scrollTop, scrollHeight, clientHeight } = tableBodyRef.current;
      // Trigger when user scrolls to within 100px of bottom
      if (scrollHeight - scrollTop - clientHeight < 100) {
        loadMoreData();
      }
    };

    const scrollContainer = tableBodyRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      return () => {
        scrollContainer.removeEventListener('scroll', handleScroll);
      };
    }
  }, [hasMore, isLoadingMore, loading, loadMoreData]);

  // Reset selected row when data changes
  useEffect(() => {
    setSelectedRowIndex(null);
  }, [displayData.length]);

  // Keyboard navigation handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't handle if user is typing in an input field
      const target = e.target as HTMLElement;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        target.isContentEditable ||
        target.closest('[contenteditable="true"]')
      ) {
        return;
      }

      // Don't handle if no data or table not visible
      if (displayData.length === 0 || !tableRef.current) return;

      // Check if table is visible in viewport
      const tableRect = tableRef.current.getBoundingClientRect();
      const isTableVisible = tableRect.height > 0 && tableRect.width > 0;

      if (!isTableVisible) return;

      // Arrow Down: Move to next row
      if (e.key === "ArrowDown" && !e.ctrlKey && !e.shiftKey && !e.altKey) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        setSelectedRowIndex((prev) => {
          const newIndex = prev === null ? 0 : Math.min(prev + 1, displayData.length - 1);
          // Scroll selected row into view
          setTimeout(() => {
            const selectedRow = document.querySelector(`tr[data-row-index="${newIndex}"]`);
            if (selectedRow) {
              selectedRow.scrollIntoView({ behavior: "smooth", block: "nearest" });
            }
          }, 0);
          return newIndex;
        });
        return;
      }

      // Arrow Up: Move to previous row
      if (e.key === "ArrowUp" && !e.ctrlKey && !e.shiftKey && !e.altKey) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        setSelectedRowIndex((prev) => {
          const newIndex = prev === null ? displayData.length - 1 : Math.max(prev - 1, 0);
          // Scroll selected row into view
          setTimeout(() => {
            const selectedRow = document.querySelector(`tr[data-row-index="${newIndex}"]`);
            if (selectedRow) {
              selectedRow.scrollIntoView({ behavior: "smooth", block: "nearest" });
            }
          }, 0);
          return newIndex;
        });
        return;
      }

      // Ctrl + E: Edit selected row (only if row is selected)
      if (e.ctrlKey && (e.key === "e" || e.key === "E") && !e.shiftKey && selectedRowIndex !== null) {
        // Only handle if table exists and has data
        if (tableRef.current && displayData.length > 0 && selectedRowIndex >= 0 && selectedRowIndex < displayData.length) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();

          // Use a more reliable way to find the edit button
          setTimeout(() => {
            const rowElement = tableRef.current?.querySelector(`tr[data-row-index="${selectedRowIndex}"]`);
            if (rowElement) {
              const editButton = rowElement.querySelector(
                '[data-edit-button="true"]:not([disabled])'
              ) as HTMLButtonElement;
              if (editButton) {
                editButton.focus();
                editButton.click();
              } else {
                // Fallback: try to find any edit button in the row
                const allButtons = rowElement.querySelectorAll('button');
                for (const btn of allButtons) {
                  if (btn.getAttribute('data-edit-button') === 'true' && !btn.disabled) {
                    btn.focus();
                    btn.click();
                    break;
                  }
                }
              }
            }
          }, 0);
          return;
        }
      }
    };

    // Use capture phase to handle before global handlers
    window.addEventListener("keydown", handleKeyDown, true);
    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [displayData, selectedRowIndex]);

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleSort = (key: string) => {
    let direction: "asc" | "desc" = "asc";
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === "asc"
    ) {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Column pinning logic with dynamic offsets
  const getPinnedColumns = useMemo(() => {
    return visibleFields.filter((field: SummaryListField) =>
      columnPinning[field.Json_Attribute] === true
    );
  }, [visibleFields, columnPinning]);

  const getColumnLeftOffset = useCallback((fieldIndex: number) => {
    const field = visibleFields[fieldIndex];
    if (!field || !columnPinning[field.Json_Attribute]) return 0;

    let offset = 0;
    for (let i = 0; i < fieldIndex; i++) {
      const prevField = visibleFields[i];
      if (prevField && columnPinning[prevField.Json_Attribute]) {
        // Estimate column width (can be improved with actual measurements)
        offset += 150; // Default column width
      }
    }
    return offset;
  }, [visibleFields, columnPinning]);

  const toggleColumnVisibility = (fieldJsonAttribute: string) => {
    setColumnVisibility((prev) => {
      const newVisibility = {
        ...prev,
        [fieldJsonAttribute]: !prev[fieldJsonAttribute],
      };
      saveTablePrefs(newVisibility, undefined);
      return newVisibility;
    });
  };

  const toggleColumnPinning = (fieldJsonAttribute: string) => {
    setColumnPinning((prev) => {
      const newPinning = {
        ...prev,
        [fieldJsonAttribute]: !prev[fieldJsonAttribute],
      };
      saveTablePrefs(undefined, newPinning);
      return newPinning;
    });
  };

  // Reset column settings to default (clear all visibility and pinning preferences)
  const resetColumnSettings = () => {
    // Clear state
    setColumnVisibility({});
    setColumnPinning({});
    // Clear saved preferences for this table
    clearTablePreferences(tableId);
  };

  // Settings popup drag handlers
  const handleSettingsDragStart = (e: React.MouseEvent) => {
    if (!settingsPopupRef.current) return;
    const rect = settingsPopupRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
    setIsDragging(true);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging || !settingsPopupRef.current) return;
      setSettingsPopupPosition({
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y,
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragOffset]);

  const handleAction = (actionId: string, rowData?: any, workflowItem?: any) => {
    if (onActionTrigger) {
      // If this is a workflow action and the state is completed, check for Action_ref_column
      if (workflowItem && workflowItem.Action_ref_column && rowData) {
        // Get the child record ID from Action_ref_column (e.g., estimate_id)
        const refColumnName = workflowItem.Action_ref_column;
        const childRecordId =
          rowData[refColumnName] ||
          rowData[refColumnName.toLowerCase()] ||
          rowData[refColumnName.toUpperCase()];

        // If child record ID exists, pass it in the rowData for edit/view mode
        if (childRecordId && String(childRecordId).trim() !== "") {
          const enhancedRowData = {
            ...rowData,
            __workflowChildId: childRecordId, // Pass child record ID
            __workflowAction: actionId,
            __workflowItem: workflowItem,
            __isEditMode: true, // Indicate this is edit/view mode
          };

          // if (import.meta.env.DEV) {
          //   console.log("[DataTable] Workflow action with child record ID:", {
          //     actionId,
          //     refColumnName,
          //     childRecordId,
          //     workflowItem: workflowItem.Name,
          //   });
          // }

          onActionTrigger(actionId, enhancedRowData);
          return;
        }
      }

      // Default behavior for add mode or when no child record exists
      onActionTrigger(actionId, rowData);
    }
  };

  const handleRefresh = () => {
    setFilters({});
  };

  // Handle report row click (for EnableReport grids)
  const handleReportRowClick = (row: Record<string, any>) => {
    if (isReportGrid && row.Filters) {
      const reportData: ReportData = {
        id: row.id,
        ReportName: row.ReportName,
        ReportSettings: row.ReportSettings,
        Filters: row.Filters,
      };
      setSelectedReport(reportData);
      setIsReportDialogOpen(true);
    }
  };

  // Handle report generation
  const handleGenerateReport = async (
    reportSettings: string,
    filterValues: Record<string, any>,
    outputType: "Excel" | "PDF" | "RawData"
  ) => {
    setIsGenerating(true);

    try {
      // Raw Data: open ReportRawDataLoader (it runs the load query internally)
      if (outputType === "RawData") {
        setRawDataLoaderParams({
          reportName: selectedReport?.ReportName || "Report",
          reportSettings,
          filterValues,
        });
        setIsGenerating(false);
        return;
      }

      const endpoint = outputType === "PDF" ? "/pabreport/pdf" : "/pabreport/excel";

      // Build the API request - use json responseType to handle Base64 encoded data
      const response = await apiService.request({
        endpoint,
        method: "POST",
        params: { name: reportSettings },
        payload: filterValues,
        includeAuthToken: true,
        responseType: "json", // Changed to json to handle Base64 response
      });

      // Handle PDF response - API returns JSON with Base64 encoded PDF
      if (outputType === "PDF") {
        let pdfBlob: Blob;

        // Check if response is already a Blob (direct PDF)
        if (response instanceof Blob) {
          pdfBlob = response;
        }
        // Check if response is JSON with Base64 data
        else if (response && typeof response === "object" && response.data) {
          const base64Data = response.data;

          if (typeof base64Data === "string" && base64Data.length > 0) {
            try {
              // Decode Base64 string to binary
              const byteCharacters = atob(base64Data);
              const byteNumbers = new Array(byteCharacters.length);
              for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
              }
              const byteArray = new Uint8Array(byteNumbers);
              pdfBlob = new Blob([byteArray], { type: "application/pdf" });
            } catch (error) {
              console.error("Failed to decode Base64 PDF:", error);
              throw new Error("Failed to decode PDF data from response");
            }
          } else {
            throw new Error("No PDF data found in response");
          }
        } else {
          throw new Error("Unexpected response format from PDF endpoint");
        }

        const pdfUrl = URL.createObjectURL(pdfBlob);
        const pdfFileName = `${reportSettings}_${new Date().toISOString().split("T")[0]}.pdf`;

        // Trigger download with proper filename so Save As gets correct name and extension
        const link = document.createElement("a");
        link.href = pdfUrl;
        link.download = pdfFileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        const newWindow = window.open(pdfUrl, "_blank");
        if (newWindow) {
          // Revoke blob URL only when the new tab is closed so Save from tab keeps working
          const interval = setInterval(() => {
            if (newWindow.closed) {
              clearInterval(interval);
              URL.revokeObjectURL(pdfUrl);
            }
          }, 500);
        } else {
          URL.revokeObjectURL(pdfUrl);
        }
      }
      // Handle Excel download
      else if (outputType === "Excel") {
        let excelBlob: Blob;

        // Check if response is already a Blob
        if (response instanceof Blob) {
          excelBlob = response;
        }
        // Check if response is JSON with Base64 data
        else if (response && typeof response === "object" && response.data) {
          const base64Data = response.data;

          if (typeof base64Data === "string" && base64Data.length > 0) {
            try {
              // Decode Base64 string to binary
              const byteCharacters = atob(base64Data);
              const byteNumbers = new Array(byteCharacters.length);
              for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
              }
              const byteArray = new Uint8Array(byteNumbers);
              excelBlob = new Blob([byteArray], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              });
            } catch (error) {
              console.error("Failed to decode Base64 Excel:", error);
              throw new Error("Failed to decode Excel data from response");
            }
          } else {
            throw new Error("No Excel data found in response");
          }
        } else {
          throw new Error("Unexpected response format from Excel endpoint");
        }

        const url = URL.createObjectURL(excelBlob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${reportSettings}_${new Date().toISOString().split("T")[0]}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }

      setIsReportDialogOpen(false);
      setSelectedReport(null);
    } catch (error) {
      console.error("Failed to generate report:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCloseReportDialog = () => {
    setIsReportDialogOpen(false);
    setSelectedReport(null);
  };

  // Calculate active filter count
  const activeFilterCount = useMemo(() => {
    return Object.values(filters).filter((value) => value && value.trim() !== "").length;
  }, [filters]);

  const hasActiveFilters = activeFilterCount > 0;

  const isFieldSortable = (field: SummaryListField): boolean => {
    return true;
  };

  const getFieldAlignment = (field: SummaryListField): "left" | "right" | "center" => {
    // Check if field has numeric control type or is a number field
    const controlType = field.ControlType?.toLowerCase() || "";
    const fieldLabel = field.Label?.toLowerCase() || "";
    const jsonAttr = field.Json_Attribute?.toLowerCase() || "";

    // Common numeric field indicators
    const numericKeywords = ["qty", "quantity", "rate", "amount", "price", "cost", "total", "vat", "tax", "discount", "percent", "%", "net"];
    const isNumericField = numericKeywords.some(keyword =>
      fieldLabel.includes(keyword) || jsonAttr.includes(keyword)
    );

    // Check ControlType for numeric fields
    if (controlType.includes("number") || controlType.includes("numeric") || isNumericField) {
      return "right";
    }

    // Center alignment for status/action-like fields
    if (fieldLabel.includes("status") || fieldLabel.includes("action")) {
      return "center";
    }

    // Default to left for text fields
    return "left";
  };

  const containerStyles: React.CSSProperties = {
    backgroundColor: config.style?.backgroundColor || "#FFFFFF",
    padding: isMobile ? 10 : config.style?.padding || 0,
    borderRadius: config.style?.borderRadius || 12,
    margin: isMobile ? 4 : config.style?.margin || 0,
    // Do not apply height/overflow here to allow pagination to stay fixed
  };

  // Professional scrollbar management: Single scrollbar container
  // Responsive max-height calculation for table data area
  const getResponsiveMaxHeight = (): string => {
    // If config has explicit maxHeight/height, use it
    if (config.style?.maxHeight || config.style?.height) {
      const explicitHeight = config.style?.maxHeight || config.style?.height;
      let heightString = typeof explicitHeight === "string" ? explicitHeight : `${explicitHeight}px`;
      // Replace calc(-350px + 100vh) with calc(-270px + 100vh)
      heightString = heightString.replace(/calc\(-350px\s*\+\s*100vh\)/gi, "calc(-270px + 100vh)");
      // Also handle equivalent format calc(100vh - 350px) -> calc(100vh - 270px)
      heightString = heightString.replace(/calc\(100vh\s*-\s*350px\)/gi, "calc(100vh - 270px)");
      return heightString;
    }

    // Responsive max-height calculation based on device type
    // Base formula: calc(100vh - offset)
    // Offset accounts for header, filters, pagination, and spacing
    if (isMobile) {
      // Mobile: smaller offset (less header space)
      return "calc(100vh - 200px)";
    } else if (isTablet) {
      // Tablet: medium offset
      return "calc(100vh - 270px)";
    } else {
      // Desktop/Laptop: standard offset (matches image requirement)
      return "calc(100vh - 275px)";
    }
  };

  const responsiveMaxHeight = getResponsiveMaxHeight();
  const hasVerticalScroll = responsiveMaxHeight !== "none";

  const tableBodyStyles: React.CSSProperties = {
    maxHeight: responsiveMaxHeight as string,
    height: config.style?.height ? String(config.style.height) : "auto",
    // Container scrolls vertically, header stays sticky
    overflowY: hasVerticalScroll ? "auto" : "visible",
    overflowX: "auto",
    // Scrollbar styling handled by CSS classes for dark mode support
  };

  // Render list view for reports
  if (isReportGrid) {
    const reportNameField = visibleFields.find(f => f.Json_Attribute === "ReportName") || visibleFields[0];
    const searchTerm = filters[reportNameField?.Json_Attribute || ""] || "";

    const filteredReports = useMemo(() => {
      if (!searchTerm) return displayData;
      const searchLower = searchTerm.toLowerCase();
      return displayData.filter((row) => {
        const reportName = row.ReportName || row[reportNameField?.Json_Attribute || ""] || "";
        return String(reportName).toLowerCase().includes(searchLower);
      });
    }, [displayData, searchTerm, reportNameField]);

    return (
      <div className="p-0 md:p-4">
        <div className="max-w-7xl mx-auto space-y-4">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              {config.DisplayName && (
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">{""}</h1>
              )}
            </div>
          </div>

          {/* Search Bar */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center justify-between gap-4 mb-2">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {filteredReports.length === 1 ? (
                  <span>Showing <span className="font-semibold text-gray-900 dark:text-gray-100">1</span> report</span>
                ) : (
                  <span>Showing <span className="font-semibold text-gray-900 dark:text-gray-100">{filteredReports.length}</span> reports</span>
                )}
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg
                  className="h-5 w-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search reports..."
                value={searchTerm}
                onChange={(e) => {
                  const newFilters = { ...filters };
                  if (e.target.value) {
                    newFilters[reportNameField?.Json_Attribute || ""] = e.target.value;
                  } else {
                    delete newFilters[reportNameField?.Json_Attribute || ""];
                  }
                  setFilters(newFilters);
                }}
                className="block w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              {/* Clear button */}
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => {
                    const newFilters = { ...filters };
                    delete newFilters[reportNameField?.Json_Attribute || ""];
                    setFilters(newFilters);
                  }}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none transition-colors"
                  title="Clear search"
                  aria-label="Clear search"
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Reports List */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            {loading ? (
              <div className="p-8 text-center">
                <div className="inline-block w-8 h-8 border-4 border-gray-200 dark:border-gray-700 border-t-primary-600 dark:border-t-primary-400 rounded-full animate-spin" />
                <p className="mt-4 text-gray-600 dark:text-gray-400">Loading reports...</p>
              </div>
            ) : error ? (
              <div className="p-8 text-center text-red-600 dark:text-red-400">{error}</div>
            ) : filteredReports.length === 0 ? (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                {searchTerm ? "No reports found matching your search." : "No reports available."}
              </div>
            ) : (
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredReports.map((row, index) => {
                  const reportName = row.ReportName || row[reportNameField?.Json_Attribute || ""] || "Untitled Report";
                  return (
                    <div
                      key={row.id || index}
                      onClick={() => handleReportRowClick(row)}
                      className="flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <div className="flex-shrink-0">
                          <svg
                            className="w-6 h-6 text-gray-400 dark:text-gray-500"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                        </div>
                        <span className="text-gray-900 dark:text-gray-100 font-medium">{reportName}</span>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleReportRowClick(row);
                        }}
                        className="flex-shrink-0 p-2 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600"
                        title="Open report"
                        aria-label="Open report"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                          />
                        </svg>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Report Filter Dialog */}
          {selectedReport && (
            <ReportFilterDialog
              report={selectedReport}
              isOpen={isReportDialogOpen}
              onClose={handleCloseReportDialog}
              onGenerate={handleGenerateReport}
              isGenerating={isGenerating}
            />
          )}

          {/* Raw Data: separate component runs load query when open */}
          <ReportRawDataLoader
            isOpen={!!rawDataLoaderParams}
            onClose={() => setRawDataLoaderParams(null)}
            reportName={rawDataLoaderParams?.reportName}
            reportSettings={rawDataLoaderParams?.reportSettings}
            filterValues={rawDataLoaderParams?.filterValues}
            loginedUserId={authUserId}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="p-0 md:p-0">
      <div className="mx-auto space-y-4">
        {/* Header with title and controls */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            {config.DisplayName && (
              <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">{config.DisplayName}</h1>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* View Switcher - Available for all datatables - Always visible */}
            <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1 shadow-sm" data-tour="view-switcher">
              <button
                onClick={() => {
                  setViewMode("table");
                  saveTablePrefs(undefined, undefined, "table", undefined);
                }}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${viewMode === "table"
                  ? "bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm"
                  : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
                  }`}
                title="Table View"
              >
                <svg className="w-4 h-4 inline-block mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Table
              </button>
              <button
                onClick={() => {
                  setViewMode("card");
                  saveTablePrefs(undefined, undefined, "card", undefined);
                }}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${viewMode === "card"
                  ? "bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm"
                  : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
                  }`}
                title="Card View"
              >
                <svg className="w-4 h-4 inline-block mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                Card
              </button>
            </div>

            {/* Table View Mode Switcher - Only when Table View is selected and workflowActions exist */}
            {viewMode === "table" && hasWorkflowActions && (
              <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1" data-tour="table-view-mode">
                <button
                  onClick={() => {
                    setTableViewMode("progress");
                    saveTablePrefs(undefined, undefined, undefined, "progress");
                  }}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${tableViewMode === "progress"
                    ? "bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm"
                    : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
                    }`}
                  title="Progress View"
                >
                  <svg className="w-4 h-4 inline-block mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  Progress
                </button>
                <button
                  onClick={() => {
                    setTableViewMode("standard");
                    saveTablePrefs(undefined, undefined, undefined, "standard");
                  }}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${tableViewMode === "standard"
                    ? "bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm"
                    : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
                    }`}
                  title="Standard View"
                >
                  <svg className="w-4 h-4 inline-block mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                  </svg>
                  Standard
                </button>
              </div>
            )}

            {/* Filters Applied Indicator */}
            {hasActiveFilters && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-primary-100 dark:bg-primary-900/50 border-2 border-primary-500 dark:border-primary-600 rounded-lg shadow-sm">
                <svg
                  className="w-4 h-4 text-primary-700 dark:text-primary-300 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                  />
                </svg>
                <span className="text-xs font-semibold text-primary-800 dark:text-primary-200 whitespace-nowrap">
                  {activeFilterCount} {activeFilterCount === 1 ? "filter" : "filters"} applied
                </span>
                <button
                  onClick={() => {
                    setFilters({});
                  }}
                  className="ml-1 p-0.5 rounded hover:bg-primary-200 dark:hover:bg-primary-800 transition-colors flex-shrink-0"
                  title="Clear all filters"
                  aria-label="Clear all filters"
                >
                  <svg
                    className="w-3.5 h-3.5 text-primary-700 dark:text-primary-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Column Settings Draggable Popup */}
        {showColumnSettings && (
          <>
            <div
              className="fixed inset-0 z-40"
              onClick={() => setShowColumnSettings(false)}
            />
            <div
              ref={settingsPopupRef}
              className="fixed bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-50 w-80 max-h-96 overflow-hidden flex flex-col"
              style={{
                left: `${settingsPopupPosition.x}px`,
                top: `${settingsPopupPosition.y}px`,
                cursor: isDragging ? 'grabbing' : 'default',
              }}
            >
              {/* Draggable Header */}
              <div
                onMouseDown={handleSettingsDragStart}
                className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-700 cursor-grab active:cursor-grabbing bg-gray-50 dark:bg-gray-900/50"
              >
                <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">COLUMN SETUP</h3>
                <div className="flex items-center gap-1">
                  {/* Reset Button */}
                  <button
                    onClick={resetColumnSettings}
                    className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-gray-500 dark:text-gray-400 hover:text-orange-600 dark:hover:text-orange-400"
                    aria-label="Reset column settings"
                    title="Reset to default"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </button>
                  {/* Close Button */}
                  <button
                    onClick={() => setShowColumnSettings(false)}
                    className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                    aria-label="Close settings"
                  >
                    <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Column List */}
              <div className="overflow-y-auto flex-1 p-2">
                {allFields.map((field: SummaryListField) => {
                  const fieldKey = field.Json_Attribute || field.BindValue || "";
                  const isVisible = columnVisibility[fieldKey] !== false;
                  const isPinned = columnPinning[fieldKey] === true;
                  return (
                    <div
                      key={fieldKey}
                      className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded"
                    >
                      {/* Column Name */}
                      <span className="text-sm text-gray-700 dark:text-gray-200 flex-1">
                        {field.Label || field.Json_Attribute || fieldKey}
                      </span>

                      {/* Pin Icon Button */}
                      <button
                        onClick={() => toggleColumnPinning(fieldKey)}
                        className={`p-1.5 rounded transition-colors ${isPinned
                          ? "text-blue-600 dark:text-blue-400"
                          : "text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                          }`}
                        title={isPinned ? "Unpin column" : "Pin column to left"}
                        aria-label={isPinned ? "Unpin column" : "Pin column"}
                      >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="12" y1="17" x2="12" y2="22"></line>
                          <path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z"></path>
                        </svg>
                      </button>

                      {/* Eye Icon Button */}
                      <button
                        onClick={() => toggleColumnVisibility(fieldKey)}
                        className={`p-1.5 rounded transition-colors ${isVisible
                          ? "text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                          : "text-gray-300 dark:text-gray-600 hover:text-gray-500 dark:hover:text-gray-400"
                          }`}
                        title={isVisible ? "Hide column" : "Show column"}
                        aria-label={isVisible ? "Hide column" : "Show column"}
                      >
                        {isVisible ? (
                          // Eye open icon (visible)
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                          </svg>
                        ) : (
                          // Eye closed icon (hidden)
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                          </svg>
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {/* Card View Search Bar - Outside wrapper to prevent disappearing */}
        {viewMode === "card" && visibleFields.length > 0 && (
          <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3 mb-3 rounded-t-xl shadow-sm">
            <div className="flex items-center gap-3">
              {/* Field Selection Dropdown */}
              <div className="relative flex-shrink-0">
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                  Search By
                </label>
                <div className="relative">
                  <select
                    key={`search-field-${selectedSearchField}`}
                    value={selectedSearchField || (visibleFields[0]?.Json_Attribute || visibleFields[0]?.BindValue || "")}
                    onChange={(e) => {
                      e.stopPropagation();
                      const newField = e.target.value;
                      setSelectedSearchField(newField);
                      selectedSearchFieldRef.current = newField;
                      userSelectedFieldRef.current = true;
                      // Clear previous field filters
                      Object.keys(filters).forEach(key => {
                        if (key !== newField && filters[key]) {
                          handleFilterChange(key, "");
                        }
                      });
                      // Clear new field filter
                      handleFilterChange(newField, "");
                    }}
                    className="appearance-none px-4 py-2.5 pr-8 text-sm font-medium border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all min-w-[180px] cursor-pointer hover:border-gray-400 dark:hover:border-gray-500"
                    title="Select field to search"
                  >
                    {visibleFields.map((field) => {
                      const fieldKey = field.Json_Attribute || field.BindValue || "";
                      const fieldLabel = field.Label || fieldKey || "Unknown";
                      return (
                        <option key={fieldKey} value={fieldKey}>
                          {fieldLabel}
                        </option>
                      );
                    })}
                  </select>
                  {/* Custom dropdown arrow */}
                  <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                    <svg
                      className="h-5 w-5 text-gray-400 dark:text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Search Input with Clear Button */}
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                  Search Value
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg
                      className="h-5 w-5 text-gray-400 dark:text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder={(() => {
                      const selectedField = selectedSearchField || (visibleFields[0]?.Json_Attribute || visibleFields[0]?.BindValue || "");
                      const field = visibleFields.find(f => (f.Json_Attribute || f.BindValue) === selectedField);
                      return `Type to search ${(field?.Label || selectedField || "").toLowerCase()}...`;
                    })()}
                    value={filters[selectedSearchField] || ""}
                    onChange={(e) => {
                      const selectedField = selectedSearchField || (visibleFields[0]?.Json_Attribute || visibleFields[0]?.BindValue || "");
                      handleFilterChange(selectedField, e.target.value);
                    }}
                    className="block w-full pl-10 pr-10 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                  />
                  {/* Clear button */}
                  {filters[selectedSearchField] ? (
                    <button
                      type="button"
                      onClick={() => {
                        const selectedField = selectedSearchField || (visibleFields[0]?.Json_Attribute || visibleFields[0]?.BindValue || "");
                        if (selectedField) {
                          handleFilterChange(selectedField, "");
                        }
                      }}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none transition-colors"
                      title="Clear search"
                      aria-label="Clear search"
                    >
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Card View - Available for all datatables */}
        {viewMode === "card" && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 flex flex-col relative transition-colors duration-200 overflow-hidden" style={{ minHeight: "500px", maxHeight: "calc(100vh - 200px)" }}>
            {loading ? (
              <div className="flex-1 flex items-center justify-center p-8">
                <div className="text-center">
                  <div className="inline-block w-8 h-8 border-4 border-gray-200 dark:border-gray-700 border-t-primary-600 dark:border-t-primary-400 rounded-full animate-spin" />
                  <p className="mt-4 text-gray-600 dark:text-gray-400">Loading...</p>
                </div>
              </div>
            ) : error ? (
              <div className="flex-1 flex items-center justify-center p-8 text-center text-red-600 dark:text-red-400">{error}</div>
            ) : displayData.length > 0 ? (
              <CardView
                data={displayData}
                visibleFields={visibleFields}
                workflowActions={workflowActions}
                summaryActions={summaryActions}
                onActionTrigger={onActionTrigger}
                onRowClick={(row, index) => {
                  setSelectedRowIndex(index);
                  // Handle row click if needed
                }}
                selectedRowIndex={selectedRowIndex}
                isLoadingMore={isLoadingMore}
                hasMore={hasMore}
                onLoadMore={useServerSidePagination ? loadMoreData : undefined}
                totalCount={totalCount}
                useServerSidePagination={useServerSidePagination}
                onProgressBarClick={(row, element) => {
                  setSelectedTimelineRow(row);
                  setTimelineAnchorElement(element);
                  setIsTimelineModalOpen(true);
                }}
                filters={filters}
                onFilterChange={undefined}
              />
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center p-10 text-center">
                <div className="bg-primary-50 dark:bg-primary-900/30 rounded-full p-3 mb-3">
                  <svg
                    className="h-8 w-8"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    style={{ color: "var(--color-primary-500, #35ba5b)" }}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                    />
                  </svg>
                </div>
                <h3 className="text-gray-900 dark:text-gray-100 font-medium text-lg">No Data Available</h3>
                <p className="text-gray-500 dark:text-gray-400 text-sm mt-1 max-w-sm">
                  No records found at this time.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Table Container - Single scrollbar approach */}
        {viewMode === "table" && (
          <div className="bg-white dark:!bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 flex flex-col relative transition-colors duration-200 overflow-hidden !mt-0" data-tour="data-table">
            <div
              ref={tableBodyRef}
              className={`custom-scrollbar overflow-x-auto overflow-y-auto`}
              style={tableBodyStyles}
            >
              <table
                ref={tableRef}
                className="w-full divide-y divide-gray-200 dark:divide-gray-700 border-separate"
                style={{ borderSpacing: 0, tableLayout: "auto", width: "100%" }}
                tabIndex={0}
              >
                <thead className="bg-gradient-to-r from-primary-600 to-primary-700 dark:!bg-gray-900 sticky top-0 z-40" data-tour="table-header">
                  <tr className="first:rounded-tl-xl last:rounded-tr-xl">
                    {visibleFields.map((field: SummaryListField, fieldIndex: number) => {
                      const stickyInfo = getStickyInfo(fieldIndex);
                      const isStickyLeft = stickyInfo?.position === "left";
                      const isFirstHeaderCell = fieldIndex === 0;
                      const isLastHeaderCell = fieldIndex === visibleFields.length - 1 && !hasActionColumn;
                      const alignment = getFieldAlignment(field);
                      const alignmentClass = alignment === "right" ? "text-right" : alignment === "center" ? "text-center" : "text-left";
                      return (
                        <th
                          key={field.Json_Attribute}
                          scope="col"
                          className={`px-4 py-2 ${alignmentClass} text-xs font-semibold text-white dark:text-gray-200 uppercase tracking-wider border-b border-primary-500/30 dark:border-gray-700 ${!isMobile && isStickyLeft
                            ? "sticky z-30 bg-gradient-to-r from-primary-600 to-primary-700 dark:!bg-gray-900"
                            : ""
                            } ${isFirstHeaderCell ? "rounded-tl-xl" : ""
                            } ${isLastHeaderCell ? "rounded-tr-xl" : ""
                            } ${isFieldSortable(field) ? "cursor-pointer hover:text-primary-100 focus-visible:ring-2 focus-visible:ring-primary-300 focus-visible:outline-none" : "cursor-default"
                            }`}
                          style={
                            !isMobile && isStickyLeft && stickyInfo
                              ? {
                                position: "sticky",
                                left: `${stickyInfo.left}px`,
                                zIndex: 30,
                                width: stickyInfo.width,
                                minWidth: stickyInfo.width,
                                maxWidth: stickyInfo.width,
                                boxShadow: "2px 0 4px rgba(0, 0, 0, 0.1)",
                              }
                              : undefined
                          }
                          onClick={() =>
                            isFieldSortable(field) && handleSort(field.Json_Attribute)
                          }
                          tabIndex={isFieldSortable(field) ? 0 : -1}
                          onKeyDown={(e) => {
                            if (isFieldSortable(field) && (e.key === "Enter" || e.key === " ")) {
                              e.preventDefault();
                              handleSort(field.Json_Attribute);
                            }
                          }}
                        >
                          <div className="flex items-center gap-1">
                            <span className="block truncate flex-1">
                              {field.Label}
                              {isFieldSortable(field) &&
                                sortConfig &&
                                sortConfig.key === field.Json_Attribute && (
                                  <span className="ml-1">
                                    {sortConfig.direction === "asc" ? "▲" : "▼"}
                                  </span>
                                )}
                            </span>
                            {columnPinning[field.Json_Attribute] === true && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleColumnPinning(field.Json_Attribute);
                                }}
                                className="flex items-center justify-center p-0.5 hover:bg-white/20 dark:hover:bg-gray-700/50 rounded transition-colors focus-visible:ring-2 focus-visible:ring-primary-300 focus-visible:outline-none cursor-pointer"
                                title="Click to unpin column"
                                aria-label="Unpin column"
                              >
                                <svg className="w-3.5 h-3.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <line x1="12" y1="17" x2="12" y2="22"></line>
                                  <path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z"></path>
                                </svg>
                              </button>
                            )}
                          </div>
                        </th>
                      );
                    })}
                    {hasActionColumn && (
                      <th
                        scope="col"
                        className={`px-4 py-2 text-xs font-semibold text-white dark:text-gray-200 uppercase tracking-wider border-b border-primary-500/30 dark:border-gray-700 bg-gradient-to-r from-primary-600 to-primary-700 dark:!bg-gray-900 rounded-tr-xl ${hasWorkflowActions && tableViewMode === "progress" ? "text-left" : "text-right"
                          } ${hasWorkflowActions && tableViewMode === "progress" ? "w-[380px] max-w-[380px]" : "whitespace-nowrap w-24"} ${!isMobile && !isInspectionScreen ? "sticky right-0 z-30 bg-gradient-to-r from-primary-600 to-primary-700 dark:!bg-gray-900" : ""
                          }`}
                        style={
                          !isMobile && !isInspectionScreen
                            ? {
                              position: "sticky",
                              right: 0,
                              zIndex: 30,
                              boxShadow: "-2px 0 4px rgba(0, 0, 0, 0.1)",
                            }
                            : undefined
                        }
                        data-tour={hasWorkflowActions && tableViewMode === "progress" ? "workflow-column" : "actions-column"}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span>{hasWorkflowActions && tableViewMode === "progress" ? "PROGRESS (CLICK TO VIEW)" : "Actions"}</span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!showColumnSettings) {
                                const buttonRect = e.currentTarget.getBoundingClientRect();
                                setSettingsPopupPosition({
                                  x: buttonRect.right - 320,
                                  y: buttonRect.bottom + 8,
                                });
                              }
                              setShowColumnSettings(!showColumnSettings);
                            }}
                            className="flex items-center justify-center p-1 hover:bg-primary-500/20 dark:hover:bg-gray-800/50 rounded transition-colors focus-visible:ring-2 focus-visible:ring-primary-300 focus-visible:outline-none"
                            title="Column settings"
                            aria-label="Column settings"
                            data-tour="column-menu"
                          >
                            <svg
                              className="w-4 h-4 text-white dark:text-gray-200"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                              />
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                              />
                            </svg>
                          </button>
                        </div>
                      </th>
                    )}
                  </tr>
                </thead>
                {/* Filter Row - Separate from thead for proper sticky positioning */}
                <thead className="bg-primary-50 dark:!bg-gray-900" style={{ position: "sticky", top: "30px", zIndex: 45 }} data-tour="table-search">
                  <tr>
                    {visibleFields.map((field: SummaryListField, fieldIndex: number) => {
                      const stickyInfo = getStickyInfo(fieldIndex);
                      const isStickyLeft = stickyInfo?.position === "left";
                      const alignment = getFieldAlignment(field);
                      const alignmentClass = alignment === "right" ? "text-right" : alignment === "center" ? "text-center" : "text-left";
                      return (
                        <td
                          key={`filter-${field.Json_Attribute}`}
                          className={`px-2 py-0.5 border-b border-primary-100 dark:border-gray-700 ${alignmentClass} ${!isMobile && isStickyLeft
                            ? "sticky bg-primary-50 dark:bg-gray-900"
                            : ""
                            }`}
                          style={
                            !isMobile && isStickyLeft && stickyInfo
                              ? {
                                position: "sticky",
                                left: `${stickyInfo.left}px`,
                                top: "30px",
                                zIndex: 45,
                                width: stickyInfo.width,
                                minWidth: stickyInfo.width,
                                maxWidth: stickyInfo.width,
                                boxShadow: "2px 0 4px rgba(0, 0, 0, 0.1)",
                              }
                              : undefined
                          }
                        >
                          {/* Show dropdown filter for select-type fields with LookupName */}
                          {field.ControlType === "Search_Select" ||
                            (field.ControlType === "TextView" && field.LookupName) ||
                            ((field.componentType === ComponentType.SELECT_DROPDOWN ||
                              field.componentType === ComponentType.SEARCHABLE_DROPDOWN) &&
                              !!field.LookupName) ? (
                            <select
                              className={`block w-full text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-md py-1 px-2 focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:outline-none focus-visible:border-primary-500 ${alignment === "right" ? "text-right" : ""}`}
                              style={{ minWidth: 0, maxWidth: "100%" }}
                              value={filters[field.Json_Attribute] || ""}
                              onChange={(e) =>
                                handleFilterChange(field.Json_Attribute, e.target.value)
                              }
                              title={`Filter by ${field.Label || field.Json_Attribute}`}
                            >
                              <option value="">
                                {fieldLoadingLookups.has(field.LookupName || "")
                                  ? "Loading..."
                                  : "Select an option"}
                              </option>
                              {field.LookupName &&
                                (fieldLookupOptions[field.LookupName] || []).map((opt, idx) => (
                                  <option key={`${field.LookupName}-${idx}-${opt.value}`} value={String(opt.value)}>
                                    {opt.label}
                                  </option>
                                ))}
                            </select>
                          ) : field.componentType === ComponentType.DATE_FIELD ? (
                            <input
                              type="date"
                              className={`block w-full text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-md py-1 px-2 focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:outline-none focus-visible:border-primary-500 ${alignment === "right" ? "text-right" : ""}`}
                              style={{ minWidth: 0, maxWidth: "100%" }}
                              value={filters[field.Json_Attribute] || ""}
                              onChange={(e) =>
                                handleFilterChange(field.Json_Attribute, e.target.value)
                              }
                              title={`Filter by ${field.Label || field.Json_Attribute} date`}
                            />
                          ) : (
                            <input
                              type="text"
                              placeholder={`Search ${(field.Label || field.Json_Attribute || "").toLowerCase()}...`}
                              className={`block w-full text-xs bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 border rounded-md py-1 px-2 text-gray-700 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:outline-none focus-visible:border-primary-500 ${alignment === "right" ? "text-right" : ""}`}
                              style={{ minWidth: 0, maxWidth: "100%" }}
                              value={filters[field.Json_Attribute] || ""}
                              onChange={(e) =>
                                handleFilterChange(field.Json_Attribute, e.target.value)
                              }
                              title={`Search and filter by ${(field.Label || field.Json_Attribute || "").toLowerCase()}`}
                            />
                          )}
                        </td>
                      );
                    })}
                    {hasActionColumn && (
                      <td
                        className={`px-1 py-0 border-b border-gray-100 dark:border-gray-700 ${hasWorkflowActions && tableViewMode === "progress" ? "text-left" : "text-center"
                          } ${!isMobile && !isInspectionScreen ? `sticky z-[45] bg-primary-50 dark:!bg-gray-900 ${hasWorkflowActions && tableViewMode === "progress" ? "w-[380px] min-w-[380px] max-w-[380px]" : "w-20 min-w-[80px]"}` : ""
                          }`}
                        style={
                          !isMobile && !isInspectionScreen
                            ? {
                              position: "sticky",
                              right: 0,
                              top: "30px",
                              zIndex: 45,
                              boxShadow: "-2px 0 4px rgba(0, 0, 0, 0.1)",
                              width: hasWorkflowActions && tableViewMode === "progress" ? "380px" : "80px",
                              minWidth: hasWorkflowActions && tableViewMode === "progress" ? "380px" : "80px",
                              maxWidth: hasWorkflowActions && tableViewMode === "progress" ? "380px" : "80px",
                            }
                            : undefined
                        }
                      >
                        {!(hasWorkflowActions && tableViewMode === "progress") && (
                          <button
                            onClick={handleRefresh}
                            className="p-0.5 text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:outline-none rounded active:scale-95 transition-transform"
                            title="Reset all filters"
                            aria-label="Reset all filters"
                            data-refresh-button="true"
                          >
                            <svg
                              className="w-3 h-3"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2.5"
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    // Skeleton loading rows - matches exact table structure
                    Array.from({ length: Math.min(pageSize, 8) }).map((_, skeletonIndex) => (
                      <tr key={`skeleton-${skeletonIndex}`} className="animate-pulse">
                        {visibleFields.map((field: SummaryListField, fieldIndex: number) => {
                          const stickyInfo = getStickyInfo(fieldIndex);
                          const isStickyLeft = stickyInfo?.position === "left";
                          const alignment = getFieldAlignment(field);
                          const alignmentClass = alignment === "right" ? "text-right" : alignment === "center" ? "text-center" : "text-left";
                          return (
                            <td
                              key={`skeleton-${skeletonIndex}-${field.Json_Attribute}`}
                              className={`px-4 py-1 text-sm ${alignmentClass} border-b border-gray-100 dark:border-gray-700 ${!isMobile && isStickyLeft
                                ? "sticky z-20 bg-white dark:bg-gray-800"
                                : ""
                                } ${!isMobile && isStickyLeft ? "whitespace-nowrap overflow-hidden" : ""}`}
                              style={
                                !isMobile && isStickyLeft && stickyInfo
                                  ? {
                                    backgroundColor: undefined,
                                    position: "sticky",
                                    left: `${stickyInfo.left}px`,
                                    zIndex: 20,
                                    width: stickyInfo.width,
                                    minWidth: stickyInfo.width,
                                    maxWidth: stickyInfo.width,
                                    boxShadow: "2px 0 4px rgba(0, 0, 0, 0.1)",
                                  }
                                  : undefined
                              }
                            >
                              <div
                                className={`skeleton-shimmer h-4 rounded ${alignment === "right" ? "ml-auto" : alignment === "center" ? "mx-auto" : ""
                                  }`}
                                style={{
                                  width: alignment === "center" ? "60%" : alignment === "right" ? "70%" : skeletonIndex % 3 === 0 ? "90%" : skeletonIndex % 3 === 1 ? "75%" : "85%",
                                }}
                              />
                            </td>
                          );
                        })}
                        {hasActionColumn && (
                          <td
                            className={`px-4 py-1.5 ${!isMobile && !isInspectionScreen ? "sticky right-0 z-20 bg-white dark:bg-gray-800" : ""
                              }`}
                            style={
                              !isMobile && !isInspectionScreen
                                ? {
                                  backgroundColor: undefined,
                                  position: "sticky",
                                  right: 0,
                                  zIndex: 20,
                                  boxShadow: "-2px 0 4px rgba(0, 0, 0, 0.1)",
                                }
                                : undefined
                            }
                          >
                            <div className="flex items-center justify-end space-x-1">
                              <div className="skeleton-shimmer h-6 w-6 rounded" />
                              <div className="skeleton-shimmer h-6 w-6 rounded" />
                            </div>
                          </td>
                        )}
                      </tr>
                    ))
                  ) : error ? (
                    <tr>
                      <td
                        colSpan={visibleFields.length + (hasActionColumn ? 1 : 0)}
                        className="border-b border-gray-300 text-center text-danger-500 p-6"
                      >
                        {error}
                      </td>
                    </tr>
                  ) : displayData.length > 0 ? (
                    (() => {
                      if (import.meta.env.DEV) {
                        // console.log("[DataTable] Rendering rows:", {
                        //   displayDataLength: displayData.length,
                        //   visibleFieldsCount: visibleFields.length,
                        //   firstRow: displayData[0],
                        //   visibleFieldsJsonAttrs: visibleFields.map(
                        //     (f) => f.Json_Attribute
                        //   ),
                        // });
                      }
                      return displayData.map((row, index) => {
                        // Get last_action and last_state from row data
                        const lastAction =
                          row?.last_action ??
                          row?.lastAction ??
                          row?.LastAction ??
                          "";
                        const lastStateRaw =
                          row?.last_state ??
                          row?.lastState ??
                          row?.LastState ??
                          row?.workflow_state ??
                          row?.workflowState;

                        const normalizedLastState =
                          typeof lastStateRaw === "string"
                            ? lastStateRaw.trim().toLowerCase()
                            : lastStateRaw !== undefined && lastStateRaw !== null
                              ? String(lastStateRaw).trim().toLowerCase()
                              : "";

                        // Get current_state for checking if workflow is closed
                        const currentStateRaw =
                          row?.current_state ??
                          row?.currentState ??
                          row?.CurrentState ??
                          "";

                        const normalizedCurrentState =
                          typeof currentStateRaw === "string"
                            ? currentStateRaw.trim().toLowerCase()
                            : currentStateRaw !== undefined && currentStateRaw !== null
                              ? String(currentStateRaw).trim().toLowerCase()
                              : "";

                        // Check if workflow is closed (either current_state or last_state is "Closed")
                        const isWorkflowClosed = normalizedCurrentState === "closed" || normalizedLastState === "closed";

                        // Map Action_ref_column names to row data keys
                        // Common patterns: "estimate_id", "inspection_id", "approved_id", etc.
                        const actionRefColumnMap: Record<string, string> = {
                          "Estimate": "estimate_id",
                          "Inspect": "inspection_id",
                          "Approve": "approved_id",
                          "Job card": "jobcard_id",
                          "QC Approve": "qc_approved_id",
                          "Create invoice": "invoice_id",
                          "Close": "closed_id",
                        };

                        // Determine completed workflow states based on last_action and Action_ref_column
                        const completedWorkflowIndices: number[] = [];

                        // First pass: Check each workflow step for completion
                        workflowActions.forEach((workflowItem, idx) => {
                          const actionMatch = lastAction &&
                            String(lastAction).trim().toLowerCase() ===
                            String(workflowItem.Action).trim().toLowerCase();

                          // Get the Action_ref_column name for this action
                          const refColumnName = workflowItem.Action_ref_column ||
                            actionRefColumnMap[workflowItem.Action] ||
                            `${workflowItem.Action.toLowerCase().replace(/\s+/g, '_')}_id`;

                          // Check if the ref_id exists in row data (various naming patterns)
                          // Also check for job_close_id as an alternative to closed_id
                          const refId = row[refColumnName] ||
                            row[refColumnName.toLowerCase()] ||
                            row[refColumnName.toUpperCase()] ||
                            row[`${refColumnName}_id`] ||
                            row[`${refColumnName.toLowerCase()}_id`] ||
                            (workflowItem.Action === "Close" ? (row["job_close_id"] || row["job_closeId"] || row["JOB_CLOSE_ID"]) : undefined);

                          const hasRefId = refId !== undefined &&
                            refId !== null &&
                            String(refId).trim() !== "";

                          // State is completed if:
                          // 1. last_action matches workflowItem.Action AND
                          // 2. The corresponding ref_id exists
                          if (actionMatch && hasRefId) {
                            completedWorkflowIndices.push(idx);
                          }
                        });

                        // Find the highest completed index
                        const currentWorkflowIndex = completedWorkflowIndices.length > 0
                          ? Math.max(...completedWorkflowIndices)
                          : -1;

                        // CRITICAL: Also check if the last workflow step is completed (fully completed workflow)
                        // If the last workflow step (e.g., "Close") has a ref_id, mark ALL previous steps as completed
                        // This handles the case when "Close" is completed - all previous steps should be green
                        // OR if current_state or last_state is "Closed", mark ALL steps as completed
                        if (workflowActions.length > 0) {
                          const lastWorkflowIndex = workflowActions.length - 1;
                          const lastWorkflowItem = workflowActions[lastWorkflowIndex];
                          const lastRefColumnName = lastWorkflowItem.Action_ref_column ||
                            actionRefColumnMap[lastWorkflowItem.Action] ||
                            `${lastWorkflowItem.Action.toLowerCase().replace(/\s+/g, '_')}_id`;

                          const lastRefId = row[lastRefColumnName] ||
                            row[lastRefColumnName.toLowerCase()] ||
                            row[lastRefColumnName.toUpperCase()] ||
                            row[`${lastRefColumnName}_id`] ||
                            row[`${lastRefColumnName.toLowerCase()}_id`] ||
                            (lastWorkflowItem.Action === "Close" ? (row["job_close_id"] || row["job_closeId"] || row["JOB_CLOSE_ID"]) : undefined);

                          const lastHasRefId = lastRefId !== undefined &&
                            lastRefId !== null &&
                            String(lastRefId).trim() !== "";

                          // If the last workflow step has a ref_id OR if workflow is closed (current_state or last_state is "Closed"),
                          // mark ALL steps as completed
                          // This ensures that when "Close" is completed, all previous steps show as green
                          if (lastHasRefId || isWorkflowClosed) {
                            for (let i = 0; i <= lastWorkflowIndex; i++) {
                              if (!completedWorkflowIndices.includes(i)) {
                                completedWorkflowIndices.push(i);
                              }
                            }
                            // Update currentWorkflowIndex to the last index since all are completed
                            if (currentWorkflowIndex < lastWorkflowIndex) {
                              // This will be used below to ensure all steps are marked as completed
                            }
                          }
                        }

                        // CRITICAL: If a later state is completed, mark all previous states as completed too
                        // This ensures that if "Estimated" is completed, "Inspected" is also marked as completed
                        // This should run AFTER checking for fully completed workflow
                        const finalCurrentWorkflowIndex = completedWorkflowIndices.length > 0
                          ? Math.max(...completedWorkflowIndices)
                          : -1;

                        if (finalCurrentWorkflowIndex !== -1) {
                          for (let i = 0; i <= finalCurrentWorkflowIndex; i++) {
                            if (!completedWorkflowIndices.includes(i)) {
                              completedWorkflowIndices.push(i);
                            }
                          }
                        }

                        // Next workflow index is the one after the current completed one
                        // Use finalCurrentWorkflowIndex which accounts for fully completed workflows
                        const nextWorkflowIndex =
                          finalCurrentWorkflowIndex !== -1 && finalCurrentWorkflowIndex < workflowActions.length - 1
                            ? finalCurrentWorkflowIndex + 1
                            : -1; // -1 means all steps are completed

                        const isSelected = selectedRowIndex === index;
                        const isLastRow = index === displayData.length - 1 && (!config.FooterControls || config.FooterControls.length === 0);
                        return (
                          <tr
                            key={row.id || index}
                            data-row-index={index}
                            className={`transition-colors group animate-fade-in ${isSelected
                              ? "bg-primary-100 dark:bg-primary-900/30 ring-2 ring-primary-500 dark:ring-primary-400"
                              : "bg-white dark:bg-gray-800 hover:bg-primary-50/40 dark:hover:bg-gray-700/50"
                              } ${isReportGrid ? "cursor-pointer" : ""} ${isLastRow ? "last:rounded-b-xl" : ""}`}
                            onClick={(e) => {
                              if (isReportGrid) {
                                handleReportRowClick(row);
                              } else {
                                // Set selected row when clicking on a row
                                setSelectedRowIndex(index);
                              }
                            }}
                          >
                            {visibleFields.map((field: SummaryListField, fieldIndex: number) => {
                              const stickyInfo = getStickyInfo(fieldIndex);
                              const isStickyLeft = stickyInfo?.position === "left";
                              const isFirstDataCell = fieldIndex === 0;
                              const isLastDataCell = fieldIndex === visibleFields.length - 1 && !hasActionColumn;
                              const isLastRow = index === displayData.length - 1 && (!config.FooterControls || config.FooterControls.length === 0);
                              const alignment = getFieldAlignment(field);
                              const alignmentClass = alignment === "right" ? "text-right" : alignment === "center" ? "text-center" : "text-left";
                              return (
                                <td
                                  key={field.Json_Attribute}
                                  className={`px-4 py-1 text-sm text-gray-700 dark:text-gray-200 ${alignmentClass} border-b border-gray-100 dark:border-gray-700 ${!isMobile && isStickyLeft
                                    ? `sticky z-20 ${isSelected ? "bg-primary-100 dark:bg-primary-900/30" : "bg-white dark:bg-gray-800"}`
                                    : ""
                                    } ${!isMobile && isStickyLeft ? "whitespace-nowrap overflow-hidden text-ellipsis" : ""} ${isLastRow && isFirstDataCell ? "rounded-bl-xl" : ""
                                    } ${isLastRow && isLastDataCell ? "rounded-br-xl" : ""
                                    }`}
                                  style={
                                    !isMobile && isStickyLeft && stickyInfo
                                      ? {
                                        backgroundColor: undefined,
                                        position: "sticky",
                                        left: `${stickyInfo.left}px`,
                                        zIndex: 20,
                                        width: stickyInfo.width,
                                        minWidth: stickyInfo.width,
                                        maxWidth: stickyInfo.width,
                                        boxShadow: "2px 0 4px rgba(0, 0, 0, 0.1)",
                                      }
                                      : undefined
                                  }
                                  onMouseEnter={(e) => {
                                    if (isStickyLeft) {
                                      e.currentTarget.style.backgroundColor = "";
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (isStickyLeft) {
                                      e.currentTarget.style.backgroundColor = "";
                                    }
                                  }}
                                  title={isStickyLeft ? String(row[field.Json_Attribute] ?? "") : undefined}
                                >
                                  {field.componentType === ComponentType.FILES && row[field.Json_Attribute] ? (
                                    (() => {
                                      const attachmentUrl = String(row[field.Json_Attribute] || '').trim();
                                      const isFile = isFileKey(attachmentUrl);

                                      // Show eye icon button with presigned URL support for all files
                                      return (
                                        <FileViewButton
                                          attachmentUrl={attachmentUrl}
                                          fieldLabel={field.Label}
                                          isFile={isFile}
                                        />
                                      );
                                    })()
                                  ) : (
                                    (() => {
                                      const rawValue = row[field.Json_Attribute];
                                      const lookupLabel = getLookupLabel(field, rawValue);
                                      return (
                                        <span className="block truncate">
                                          {lookupLabel ?? (rawValue ?? "")}
                                        </span>
                                      );
                                    })()
                                  )}
                                </td>
                              );
                            })}
                            {hasActionColumn && (
                              <td
                                className={`px-4 py-3 ${!isMobile && !isInspectionScreen ? "sticky right-0 z-20 bg-white dark:bg-gray-800" : ""
                                  } ${!isMobile && isSelected ? "bg-primary-100 dark:bg-primary-900/30" : ""} ${index === displayData.length - 1 && (!config.FooterControls || config.FooterControls.length === 0) ? "rounded-br-xl" : ""
                                  }`}
                                style={
                                  !isMobile && !isInspectionScreen
                                    ? {
                                      backgroundColor: undefined,
                                      position: "sticky",
                                      right: 0,
                                      zIndex: 20,
                                      boxShadow: "-2px 0 4px rgba(0, 0, 0, 0.1)",
                                    }
                                    : undefined
                                }
                                onMouseEnter={(e) => {
                                  if (!isMobile) {
                                    e.currentTarget.style.backgroundColor = "";
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!isMobile) {
                                    e.currentTarget.style.backgroundColor = "";
                                  }
                                }}
                              >
                                {hasWorkflowActions && tableViewMode === "progress" ? (
                                  // Custom Progress Column (Progress Mode)
                                  (() => {
                                    const totalSteps = workflowActions.length;
                                    const currentStep = finalCurrentWorkflowIndex + 1;
                                    const progressPercentage = totalSteps > 0
                                      ? Math.round(((finalCurrentWorkflowIndex + 1) / totalSteps) * 100)
                                      : 0;

                                    // Get current and next workflow items
                                    const currentWorkflowItem = finalCurrentWorkflowIndex >= 0 && finalCurrentWorkflowIndex < workflowActions.length
                                      ? workflowActions[finalCurrentWorkflowIndex]
                                      : null;
                                    const nextWorkflowItem = nextWorkflowIndex >= 0 && nextWorkflowIndex < workflowActions.length
                                      ? workflowActions[nextWorkflowIndex]
                                      : null;

                                    // Determine current status label
                                    let currentStatusLabel = "Not Started";
                                    if (currentWorkflowItem) {
                                      if (normalizedLastState && currentWorkflowItem.States) {
                                        const matchingState = currentWorkflowItem.States.find(
                                          (s) => String(s).trim().toLowerCase() === normalizedLastState
                                        );
                                        currentStatusLabel = matchingState || currentWorkflowItem.Label;
                                      } else {
                                        currentStatusLabel = currentWorkflowItem.Label;
                                      }
                                    }

                                    // Determine next status label
                                    const nextStatusLabel = nextWorkflowItem
                                      ? (nextWorkflowItem.Action || nextWorkflowItem.Label)
                                      : "Completed";

                                    // Get status colors
                                    const currentStatusColor = finalCurrentWorkflowIndex >= 0
                                      ? (normalizedLastState === "rejected" ? "text-red-600 dark:text-red-400" : "text-primary-600 dark:text-primary-400")
                                      : "text-gray-500 dark:text-gray-400";
                                    const nextStatusColor = "text-orange-600 dark:text-orange-400";

                                    // Build workflow steps for timeline
                                    const workflowSteps: WorkflowStep[] = workflowActions.map((item, idx) => {
                                      const isCompleted = completedWorkflowIndices.includes(idx);
                                      const isPending = idx === nextWorkflowIndex;

                                      return {
                                        name: item.Name,
                                        label: item.Label,
                                        action: item.Action,
                                        status: isCompleted ? "completed" : isPending ? "pending" : "upcoming",
                                        refColumnName: item.Action_ref_column,
                                      };
                                    });

                                    // Get progress bar color based on progress percentage
                                    const getProgressBarColor = () => {
                                      if (progressPercentage >= 85) {
                                        return "bg-emerald-500 dark:bg-emerald-600"; // Green for near completion
                                      } else if (progressPercentage >= 60) {
                                        return "bg-blue-500 dark:bg-blue-600"; // Blue for good progress
                                      } else if (progressPercentage >= 40) {
                                        return "bg-amber-500 dark:bg-amber-600"; // Amber for moderate progress
                                      } else if (progressPercentage >= 20) {
                                        return "bg-orange-500 dark:bg-orange-600"; // Orange for early progress
                                      } else {
                                        return "bg-red-500 dark:bg-red-600"; // Red for very early stage
                                      }
                                    };

                                    // Get status tag color based on current status label
                                    const getStatusTagColor = () => {
                                      const statusLower = currentStatusLabel.toLowerCase();

                                      // QC Approved - Blue
                                      if (statusLower.includes("qc approved") || statusLower.includes("qc approve")) {
                                        return "bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-800";
                                      }
                                      // Invoiced - Purple
                                      if (statusLower.includes("invoiced") || statusLower.includes("invoice")) {
                                        return "bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-800";
                                      }
                                      // Closed - Emerald/Green
                                      if (statusLower.includes("closed") || statusLower.includes("close")) {
                                        return "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300 hover:bg-emerald-200 dark:hover:bg-emerald-800";
                                      }
                                      // Approved - Green
                                      if (statusLower.includes("approved") || statusLower.includes("approve")) {
                                        return "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-800";
                                      }
                                      // Estimated - Cyan
                                      if (statusLower.includes("estimated") || statusLower.includes("estimate")) {
                                        return "bg-cyan-100 dark:bg-cyan-900/30 text-cyan-800 dark:text-cyan-300 hover:bg-cyan-200 dark:hover:bg-cyan-800";
                                      }
                                      // Job card - Indigo
                                      if (statusLower.includes("job card") || statusLower.includes("jobcard")) {
                                        return "bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300 hover:bg-indigo-200 dark:hover:bg-indigo-800";
                                      }
                                      // Rejected - Red
                                      if (statusLower.includes("rejected") || statusLower.includes("reject")) {
                                        return "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-800";
                                      }
                                      // Inspected - Amber/Yellow (default for early stages)
                                      return "bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-800";
                                    };

                                    return (
                                      <div className="w-full">
                                        {/* Main Row: Status Tag, Progress Bar, and Next Action Button */}
                                        <div className="flex items-start gap-3">
                                          {/* Left Side: Status Tag and Progress Bar */}
                                          <div className="flex-1 min-w-0 flex flex-col gap-1.5">
                                            {/* Status Tag - Standardized pill shape with dynamic colors based on status */}
                                            {currentWorkflowItem && (
                                              <button
                                                onClick={() => handleAction(currentWorkflowItem.Action, row, currentWorkflowItem)}
                                                className={`text-xs font-medium px-3 py-1 rounded-full transition-all duration-200 active:scale-95 cursor-pointer whitespace-nowrap inline-block w-fit ${getStatusTagColor()}`}
                                                title={`Current status: ${currentStatusLabel}`}
                                              >
                                                {currentStatusLabel}
                                              </button>
                                            )}

                                            {/* Progress Bar - Standardized width and dynamic colors */}
                                            <div className="w-[200px] flex-shrink-0">
                                              <div
                                                ref={(el) => {
                                                  if (el) progressBarRefs.current.set(index, el);
                                                }}
                                                className="relative w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
                                                onClick={(e) => {
                                                  setSelectedTimelineRow(row);
                                                  setTimelineAnchorElement(e.currentTarget);
                                                  setIsTimelineModalOpen(true);
                                                }}
                                                title="Click to view timeline"
                                              >
                                                <div
                                                  className={`h-1.5 rounded-full transition-all duration-500 ${getProgressBarColor()}`}
                                                  style={{ width: `${progressPercentage}%` }}
                                                ></div>
                                              </div>
                                            </div>
                                          </div>

                                          {/* Right Side: Next Action Button with Arrow and Step Count - Standardized */}
                                          {nextWorkflowItem && (() => {
                                            const isCloseAction = nextWorkflowItem.Action === "Close" || nextWorkflowItem.Name === "Close";
                                            const issueStatus = row["issue_status"] || row["issueStatus"] || row["ISSUE_STATUS"];
                                            const isDisabled = isCloseAction && (issueStatus === "Pending" || String(issueStatus).toLowerCase() === "pending");

                                            return (
                                              <div className="flex items-center gap-2 flex-shrink-0">
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (!isDisabled) {
                                                      handleAction(nextWorkflowItem.Action, row, nextWorkflowItem);
                                                    }
                                                  }}
                                                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg ${isDisabled
                                                    ? "bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed opacity-60"
                                                    : "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30 transition-all duration-200 active:scale-95 cursor-pointer"
                                                    } whitespace-nowrap`}
                                                  disabled={isDisabled}
                                                  title={isDisabled && isCloseAction ? "Cannot close jobcard when part issue is pending" : `Click to ${nextStatusLabel}`}
                                                >
                                                  <span className="text-xs font-medium">{nextStatusLabel}</span>
                                                  <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                                  </svg>
                                                </button>
                                                <span className="text-xs text-gray-400 dark:text-gray-500 font-mono flex-shrink-0">
                                                  {currentStep}/{totalSteps}
                                                </span>
                                              </div>
                                            );
                                          })()}
                                        </div>
                                      </div>
                                    );
                                  })()
                                ) : (
                                  // Standard Action Column (Standard Mode or other screens)
                                  <div className={`flex items-center space-x-1 ${workflowActions.length === 0 && summaryActions.length > 0 ? "justify-end" : ""
                                    }`}>
                                    {workflowActions.map(
                                      (workflowItem, workflowIndex) => {
                                        let backgroundColor =
                                          workflowColorPalette.default;
                                        let textColor = workflowColorPalette.textLight;
                                        let fontWeight: React.CSSProperties["fontWeight"] = 500;
                                        let status:
                                          | "completed"
                                          | "pending"
                                          | "upcoming"
                                          | "default" = "default";
                                        let displayLabel = workflowItem.Label;

                                        // Check if this state is completed
                                        const isCompleted = completedWorkflowIndices.includes(workflowIndex);

                                        // Check if this is the next available state
                                        const isNextState = workflowIndex === nextWorkflowIndex;

                                        // Determine display label based on state status
                                        // CRITICAL: If workflow is closed and this is the last step (Close action), show "Closed"
                                        if (isWorkflowClosed && workflowItem.Action === "Close" && workflowItem.States && workflowItem.States.includes("Closed")) {
                                          displayLabel = "Closed";
                                        } else if (isNextState) {
                                          // For pending/next states (orange), show the Action (e.g., "Estimate")
                                          displayLabel = workflowItem.Action || workflowItem.Label;
                                        } else if (isCompleted && normalizedLastState && workflowItem.States) {
                                          // For completed states (green), show the matching state from States array
                                          const matchingState = workflowItem.States.find(
                                            (s) => String(s).trim().toLowerCase() === normalizedLastState
                                          );
                                          if (matchingState) {
                                            displayLabel = matchingState;
                                          } else {
                                            // Fallback to first state from States array or Label
                                            displayLabel = workflowItem.States[0] || workflowItem.Label;
                                          }
                                        } else {
                                          // For upcoming states (gray), show the Action (e.g., "Create invoice") if available, otherwise Label
                                          displayLabel = workflowItem.Action || workflowItem.Label;
                                        }

                                        // Check EnableOn conditions for next state
                                        let isEnabled = true;
                                        if (isNextState && workflowItem.EnableOn && workflowItem.EnableOn.length > 0) {
                                          // CRITICAL: Check if the actual last_state matches any EnableOn state
                                          // This ensures that "Job card" is only enabled when state is "Approved", not "Rejected" or "Put on hold"
                                          const normalizedEnableOnStates = workflowItem.EnableOn.map((s) =>
                                            String(s).trim().toLowerCase()
                                          );

                                          // Check if current last_state matches any EnableOn state
                                          const stateMatchesEnableOn = normalizedEnableOnStates.includes(normalizedLastState);

                                          // Also check if any of the EnableOn workflow steps are completed (for backward compatibility)
                                          const enableOnIndices = workflowItem.EnableOn.map((enableState) =>
                                            workflowActions.findIndex((w) =>
                                              w.States?.some((s) =>
                                                String(s).trim().toLowerCase() ===
                                                String(enableState).trim().toLowerCase()
                                              )
                                            )
                                          );
                                          const workflowStepCompleted = enableOnIndices.some((idx) =>
                                            completedWorkflowIndices.includes(idx)
                                          );

                                          // Enable only if:
                                          // 1. The actual state matches EnableOn states (e.g., "Approved" matches EnableOn: ["Approved"])
                                          // 2. OR the workflow step is completed AND we don't have a conflicting state
                                          //    (e.g., if last_state is "Rejected", don't enable even if workflow step is completed)
                                          if (normalizedLastState) {
                                            // If we have a last_state, it must match EnableOn states
                                            isEnabled = stateMatchesEnableOn;
                                          } else {
                                            // If no last_state, fall back to workflow step completion check
                                            isEnabled = workflowStepCompleted;
                                          }

                                          if (import.meta.env.DEV && workflowItem.Action === "Job card") {
                                            // console.log("[DataTable] Job card enable check:", {
                                            //   workflowItem: workflowItem.Action,
                                            //   EnableOn: workflowItem.EnableOn,
                                            //   normalizedLastState,
                                            //   normalizedEnableOnStates,
                                            //   stateMatchesEnableOn,
                                            //   workflowStepCompleted,
                                            //   isEnabled,
                                            //   lastStateRaw,
                                            // });
                                          }
                                        }



                                        // Check if THIS SPECIFIC workflow button displays "Rejected"
                                        // Only apply red color if this button's displayLabel is "Rejected"
                                        const isRejectedButton = String(displayLabel).toLowerCase() === "rejected";

                                        if (isCompleted) {
                                          // Only apply red color if THIS button shows "Rejected"
                                          // Previous completed steps (like "Inspected", "Estimated") remain green
                                          if (isRejectedButton) {
                                            backgroundColor = workflowColorPalette.rejected;
                                            textColor = workflowColorPalette.textLight;
                                          } else {
                                            backgroundColor = workflowColorPalette.completed;
                                          }
                                          status = "completed";
                                          // Use the final current workflow index for determining which step to highlight
                                          if (workflowIndex === finalCurrentWorkflowIndex) {
                                            fontWeight = 600;
                                          }
                                        } else if (isNextState && isEnabled) {
                                          backgroundColor = workflowColorPalette.pending;
                                          status = "pending";
                                          fontWeight = 600;
                                        } else {
                                          backgroundColor = workflowColorPalette.upcoming;
                                          textColor = workflowColorPalette.textDark;
                                          status = "upcoming";
                                        }

                                        const isLastStep = workflowIndex === workflowActions.length - 1;
                                        const connectorColor = isCompleted
                                          ? "bg-primary-200"
                                          : isNextState && isEnabled
                                            ? "bg-orange-200"
                                            : "bg-gray-200";

                                        return (
                                          <div key={`workflow-${workflowItem.Name}`} className="flex items-center group">
                                            {isCompleted ? (
                                              <button
                                                className="flex items-center px-2.5 py-1 my-2 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 border border-primary-200 dark:border-primary-700 shadow-sm cursor-pointer hover:bg-primary-200 dark:hover:bg-primary-900/50 transition-all focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:outline-none active:scale-95 ripple-effect"
                                                onClick={() => handleAction(workflowItem.Action, row, workflowItem)}
                                                title={workflowItem.Label || `View ${workflowItem.Action}`}
                                              >
                                                <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                                </svg>
                                                <span className="text-xs font-medium whitespace-nowrap">{displayLabel}</span>
                                              </button>
                                            ) : isNextState && isEnabled ? (() => {
                                              const isCloseAction = workflowItem.Action === "Close" || workflowItem.Name === "Close";
                                              const issueStatus = row["issue_status"] || row["issueStatus"] || row["ISSUE_STATUS"];
                                              const isPartIssuePending = isCloseAction && (issueStatus === "Pending" || String(issueStatus).toLowerCase() === "pending");

                                              return (
                                                <button
                                                  className={`flex items-center px-2.5 py-1 rounded-full ${isPartIssuePending
                                                    ? "bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 border border-gray-200 dark:border-gray-700 cursor-not-allowed opacity-60 shadow-none ring-0"
                                                    : "bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-300 border border-orange-200 dark:border-orange-700 shadow-md ring-2 ring-orange-100 dark:ring-orange-900/50 ring-offset-1 hover:scale-105 transition-transform focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:outline-none active:scale-95 cursor-pointer ripple-effect"
                                                    }`}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (!isPartIssuePending) {
                                                      handleAction(workflowItem.Action, row, workflowItem);
                                                    }
                                                  }}
                                                  disabled={isPartIssuePending}
                                                  title={isPartIssuePending && isCloseAction ? "Cannot close jobcard when part issue is pending" : (workflowItem.Label || `${isEnabled ? "Click to" : "Cannot"} ${workflowItem.Action}`)}
                                                >
                                                  <svg className="w-3.5 h-3.5 mr-1.5 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                  </svg>
                                                  <span className="text-xs font-bold whitespace-nowrap">{displayLabel}</span>
                                                </button>
                                              );
                                            })() : (
                                              <div className={`flex items-center px-2.5 py-1 rounded-full bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-600 ${isEnabled || isCompleted ? "" : "opacity-40"} border-dashed`}>
                                                <span className="w-3 h-3 rounded-full border border-gray-400 dark:border-gray-500 mr-2 block"></span>
                                                <span className="text-xs whitespace-nowrap">{displayLabel}</span>
                                              </div>
                                            )}
                                            {!isLastStep && (
                                              <div className={`w-4 h-0.5 mx-1 ${connectorColor}`}></div>
                                            )}
                                          </div>
                                        );
                                      }
                                    )}
                                    {summaryActions.length > 0 && (
                                      <div className="flex items-center gap-1.5">
                                        {summaryActions.map(
                                          (summaryAction: SummaryListActionConfig, idx) => {
                                            // Role-based visibility support (screen JSON uses `visibility: [roleIds...]`; support typo "visiblity")
                                            const actionVisibility = ((summaryAction as any).visibility ?? (summaryAction as any).visiblity) as number[] | undefined;
                                            const legacyVisibleForRoles = (summaryAction as any).visibleForRoles as number[] | undefined;
                                            if (!isComponentVisible(actionVisibility, legacyVisibleForRoles)) {
                                              return null;
                                            }

                                            const actionId =
                                              summaryAction.Action?.Action ||
                                              summaryAction.ActionType;
                                            const buttonLabel =
                                              summaryAction.ActionType ||
                                              actionId ||
                                              "Action";
                                            const actionTypeValue = String(
                                              summaryAction.ActionType ?? ""
                                            );
                                            const actionTypeLower = actionTypeValue
                                              .trim()
                                              .toLowerCase();
                                            const iconSize = 14;
                                            const icon = getSummaryActionIcon(
                                              actionTypeLower,
                                              iconSize
                                            );

                                            if (!actionId) {
                                              return null;
                                            }

                                            // Status-based visibility support (e.g. visibleForStatus: ["pending"])
                                            const visibleForStatus = (summaryAction as any).visibleForStatus as string[] | undefined;
                                            if (Array.isArray(visibleForStatus) && visibleForStatus.length > 0) {
                                              const rowStatusRaw =
                                                (row as any).status ??
                                                (row as any).current_state ??
                                                (row as any).currentStatus ??
                                                "";
                                              const rowStatus = String(rowStatusRaw).trim().toLowerCase();
                                              const allowed = visibleForStatus
                                                .map((s) => String(s).trim().toLowerCase())
                                                .includes(rowStatus);
                                              if (!allowed) {
                                                return null;
                                              }
                                            }

                                            // Determine border and icon colors based on action type - outlined style
                                            let borderColorClass = "border-green-500";
                                            let iconColorClass = "text-green-500";
                                            let hoverBgClass = "hover:bg-green-50 dark:hover:bg-green-900/20";

                                            if (actionTypeLower === "view") {
                                              borderColorClass = "border-blue-500";
                                              iconColorClass = "text-blue-500";
                                              hoverBgClass = "hover:bg-blue-50 dark:hover:bg-blue-900/20";
                                            } else if (actionTypeLower === "edit") {
                                              borderColorClass = "border-green-500";
                                              iconColorClass = "text-green-500";
                                              hoverBgClass = "hover:bg-green-50 dark:hover:bg-green-900/20";
                                            } else if (actionTypeLower === "delete") {
                                              borderColorClass = "border-red-500";
                                              iconColorClass = "text-red-500";
                                              hoverBgClass = "hover:bg-red-50 dark:hover:bg-red-900/20";
                                            } else if (actionTypeLower === "pdf_download" || actionTypeLower === "print" || actionTypeLower === "view_report") {
                                              borderColorClass = "border-blue-500";
                                              iconColorClass = "text-blue-500";
                                              hoverBgClass = "hover:bg-blue-50 dark:hover:bg-blue-900/20";
                                            } else if (actionTypeLower === "lock") {
                                              borderColorClass = "border-orange-500";
                                              iconColorClass = "text-orange-500";
                                              hoverBgClass = "hover:bg-orange-50 dark:hover:bg-orange-900/20";
                                            } else if (actionTypeLower === "approve") {
                                              borderColorClass = "border-emerald-500";
                                              iconColorClass = "text-emerald-600";
                                              hoverBgClass = "hover:bg-emerald-50 dark:hover:bg-emerald-900/20";
                                            }

                                            return (
                                              <React.Fragment key={`summary-${summaryAction.ActionType || idx}`}>
                                                <button
                                                  className={`flex items-center justify-center w-7 h-7 bg-white dark:bg-gray-800 border ${borderColorClass} ${iconColorClass} rounded transition-all focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:outline-none active:scale-95 ${hoverBgClass}`}
                                                  onClick={() => {
                                                    const actionPayload = {
                                                      ...row,
                                                      __summaryAction: summaryAction,
                                                    };
                                                    handleAction(actionId, actionPayload);
                                                  }}
                                                  title={
                                                    summaryAction.tooltip ||
                                                    buttonLabel ||
                                                    `Click to ${actionTypeLower}`
                                                  }
                                                  aria-label={buttonLabel || `Click to ${actionTypeLower}`}
                                                  {...(actionTypeLower === "pdf_download" || actionTypeLower === "print" || actionId === "pdf" ? { "data-pdf-button": "true", "data-action-id": actionId } : {})}
                                                  {...(actionTypeLower === "edit" ? { "data-edit-button": "true", "data-action-id": actionId } : {})}
                                                  {...(actionTypeLower === "delete" ? { "data-delete-button": "true", "data-action-id": actionId } : {})}
                                                >
                                                  {icon && (
                                                    <span className="flex items-center justify-center">
                                                      {icon}
                                                    </span>
                                                  )}
                                                </button>
                                              </React.Fragment>
                                            );
                                          }
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </td>
                            )}
                          </tr>
                        );
                      });
                    })()
                  ) : (
                    <tr>
                      <td
                        colSpan={visibleFields.length + (hasActionColumn ? 1 : 0)}
                        className="border-b border-gray-300"
                      >
                        <div className="p-10 flex flex-col items-center justify-center text-center">
                          <div className="bg-primary-50 dark:bg-primary-900/30 rounded-full p-3 mb-3">
                            <svg
                              className="h-8 w-8"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              style={{ color: "var(--color-primary-500, #35ba5b)" }}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                              />
                            </svg>
                          </div>
                          <h3 className="text-gray-900 dark:text-gray-100 font-medium text-lg">No Data Available</h3>
                          <p className="text-gray-500 dark:text-gray-400 text-sm mt-1 max-w-sm">
                            No records found at this time.
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
                {/* Footer row for totals */}
                {config.FooterControls &&
                  config.FooterControls.length > 0 &&
                  filteredData.length > 0 && (
                    <tfoot className="sticky bottom-0 z-30 bg-gray-50 dark:bg-gray-900/50">
                      <tr className="bg-gray-50 dark:bg-gray-900/50 border-t-2 border-gray-300 dark:border-gray-700">
                        {(() => {
                          // Calculate footer values
                          const footerValues: Record<string, number> = {};

                          config.FooterControls.forEach((footerControl: any) => {
                            const expression = footerControl.ExpressionV2;
                            if (expression && expression.includes("[SUM:")) {
                              const sumMatch = expression.match(/\[SUM:(\w+)\]/);
                              if (sumMatch) {
                                const fieldName = sumMatch[1];
                                // Sum across all data (not just filtered/paginated)
                                const sum = data.reduce((acc, row) => {
                                  // Try multiple field name variations
                                  const value =
                                    parseFloat(row[fieldName]) ||
                                    parseFloat(row[fieldName.toLowerCase()]) ||
                                    parseFloat(row[fieldName.toUpperCase()]) ||
                                    0;
                                  return acc + value;
                                }, 0);
                                footerValues[footerControl.BindValue] = sum;
                              }
                            }
                          });

                          // Render footer cells - align with visible fields
                          // Map FooterControls to match the column order
                          const visibleFieldNames = visibleFields.map(
                            (f) => f.BindValue || f.Json_Attribute || ""
                          );

                          return config.FooterControls.map(
                            (footerControl: any, index: number) => {
                              const isHidden =
                                footerControl.style?.display === "none" ||
                                (!footerControl.Label && !footerControl.ExpressionV2);
                              const bindValue = footerControl.BindValue;
                              const label = footerControl.Label || "";
                              const value =
                                footerValues[bindValue] ||
                                footerControl.Default_Value ||
                                "0";
                              const formattedValue =
                                typeof value === "number"
                                  ? parseFloat(value.toFixed(2))
                                  : value;

                              if (isHidden) {
                                return (
                                  <td
                                    key={index}
                                    className="p-3 border-b border-gray-200"
                                  ></td>
                                );
                              }

                              const isFirstFooterCell = index === 0;
                              const footerControlsLength = config.FooterControls?.length || 0;
                              const isLastFooterCell = index === footerControlsLength - 1 && !hasActionColumn;
                              return (
                                <td
                                  key={index}
                                  className={`p-3 border-b border-gray-200 dark:border-gray-700 font-semibold text-right text-red-600 ${isFirstFooterCell ? "rounded-bl-xl" : ""
                                    }`}
                                >
                                  {formattedValue}
                                </td>
                              );
                            }
                          );
                        })()}
                        {hasActionColumn && (
                          <td className="p-3 border-b border-gray-200 dark:border-gray-700 rounded-br-xl"></td>
                        )}
                      </tr>
                    </tfoot>
                  )}
                {/* Loading Spinner at Bottom of Table Body */}
                {isLoadingMore && (
                  <tr>
                    <td
                      colSpan={visibleFields.length + (hasActionColumn ? 1 : 0)}
                      className="px-4 py-6 text-center border-b border-gray-200 dark:border-gray-700"
                    >
                      <div className="flex items-center justify-center">
                        <div className="inline-block w-6 h-6 border-3 border-gray-200 dark:border-gray-700 border-t-primary-600 dark:border-t-primary-400 rounded-full animate-spin" />
                        <span className="ml-3 text-sm text-gray-600 dark:text-gray-400">Loading more records...</span>
                      </div>
                    </td>
                  </tr>
                )}
              </table>
            </div>

            {/* Sticky Footer with Progress Bar */}
            <div className="sticky bottom-0 left-0 right-0 px-5 py-2.5 border-t border-gray-200 dark:border-gray-700 bg-white dark:!bg-gray-800 z-30">
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Showing <strong className="text-gray-700 dark:text-gray-200">{displayData.length}</strong> of <strong className="text-gray-700 dark:text-gray-200">{useServerSidePagination ? totalCount : filteredData.length}</strong> records
                </span>
                <div
                  className="h-2 rounded-[10px] overflow-hidden"
                  style={{
                    width: '250px',
                    backgroundColor: 'rgba(40, 167, 69, 0.15)'
                  }}
                >
                  <div
                    className="h-full rounded-[10px] transition-all duration-300"
                    style={{
                      width: `${useServerSidePagination
                        ? (totalCount > 0 ? (displayData.length / totalCount) * 100 : 0)
                        : (filteredData.length > 0 ? (displayData.length / filteredData.length) * 100 : 0)
                        }%`,
                      backgroundColor: '#28a745'
                    }}
                  />
                </div>
                {hasMore && !isLoadingMore && (
                  <span className="text-sm text-gray-500 dark:text-gray-400 ml-auto">Scroll for more</span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Report Filter Dialog */}
        {selectedReport && (
          <ReportFilterDialog
            report={selectedReport}
            isOpen={isReportDialogOpen}
            onClose={handleCloseReportDialog}
            onGenerate={handleGenerateReport}
            isGenerating={isGenerating}
          />
        )}

        {/* Raw Data: separate component runs load query when open */}
        <ReportRawDataLoader
          isOpen={!!rawDataLoaderParams}
          onClose={() => setRawDataLoaderParams(null)}
          reportName={rawDataLoaderParams?.reportName}
          reportSettings={rawDataLoaderParams?.reportSettings}
          filterValues={rawDataLoaderParams?.filterValues}
          loginedUserId={authUserId}
        />

        {/* Timeline Modal - Available for all datatables with workflow actions */}
        {hasWorkflowActions && selectedTimelineRow && (
          <TimelineModal
            isOpen={isTimelineModalOpen}
            onClose={() => {
              setIsTimelineModalOpen(false);
              setSelectedTimelineRow(null);
              setTimelineAnchorElement(null);
            }}
            vehicleNo={selectedTimelineRow.platenumber || selectedTimelineRow.plate_number || "N/A"}
            workflowSteps={workflowActions.map((item, idx) => {
              if (!selectedTimelineRow) {
                return {
                  name: item.Name,
                  label: item.Label,
                  action: item.Action,
                  status: "upcoming" as const,
                  refColumnName: item.Action_ref_column,
                };
              }

              const isCompleted = (() => {
                // Check if this step has a ref column ID (e.g., inspection_id, estimate_id)
                // If it has an ID, it means the step was completed, regardless of last_action
                const refColumnName = item.Action_ref_column ||
                  `${item.Action.toLowerCase().replace(/\s+/g, '_')}_id`;

                const refId = selectedTimelineRow[refColumnName] ||
                  selectedTimelineRow[refColumnName.toLowerCase()] ||
                  selectedTimelineRow[refColumnName.toUpperCase()];

                const hasRefId = refId !== undefined &&
                  refId !== null &&
                  String(refId).trim() !== "";

                // A step is completed if it has a ref column ID
                // This allows navigation to all previous completed steps in edit mode
                return hasRefId;
              })();

              const isPending = idx === (() => {
                const completedIndices: number[] = [];
                workflowActions.forEach((wItem, wIdx) => {
                  const actionMatch = selectedTimelineRow.last_action &&
                    String(selectedTimelineRow.last_action).trim().toLowerCase() ===
                    String(wItem.Action).trim().toLowerCase();

                  const refColumnName = wItem.Action_ref_column ||
                    `${wItem.Action.toLowerCase().replace(/\s+/g, '_')}_id`;

                  const refId = selectedTimelineRow[refColumnName] ||
                    selectedTimelineRow[refColumnName.toLowerCase()] ||
                    selectedTimelineRow[refColumnName.toUpperCase()];

                  const hasRefId = refId !== undefined &&
                    refId !== null &&
                    String(refId).trim() !== "";

                  if (actionMatch && hasRefId) {
                    completedIndices.push(wIdx);
                  }
                });

                const maxCompleted = completedIndices.length > 0 ? Math.max(...completedIndices) : -1;
                return maxCompleted >= 0 && maxCompleted < workflowActions.length - 1 ? maxCompleted + 1 : -1;
              })();

              return {
                name: item.Name,
                label: item.Label,
                action: item.Action,
                status: isCompleted ? "completed" : isPending ? "pending" : "upcoming",
                refColumnName: item.Action_ref_column,
              };
            })}
            rowData={selectedTimelineRow}
            anchorElement={timelineAnchorElement}
            onWorkflowStepClick={(step, rowData) => {
              // Find the workflow item and trigger the action
              const workflowItem = workflowActions.find(w => w.Name === step.name || w.Action === step.action);
              if (workflowItem) {
                handleAction(workflowItem.Action, rowData, workflowItem);
                setIsTimelineModalOpen(false);
                setSelectedTimelineRow(null);
                setTimelineAnchorElement(null);
              }
            }}
          />
        )}
      </div>
    </div>
  );
};
